export type ModuleStatus = "completed" | "current" | "upcoming";

const MONTH_NAMES = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

/**
 * Derives a module's status from its `month` field and today's date, rather
 * than a hand-maintained field — so it can't silently go stale. A module is
 * "current" during its named month, "completed" once that month has passed,
 * and "upcoming" before it. Comparing by month-of-year only (ignoring the
 * year) also means the whole cycle naturally restarts every January for the
 * next cohort.
 */
export function getModuleStatus(
  module: Pick<Module, "month">,
  today: Date = new Date(),
): ModuleStatus {
  const moduleMonthIndex = MONTH_NAMES.indexOf(module.month.trim().toLowerCase());
  if (moduleMonthIndex === -1) {
    return "upcoming";
  }
  const currentMonthIndex = today.getMonth();
  if (moduleMonthIndex < currentMonthIndex) {
    return "completed";
  }
  if (moduleMonthIndex === currentMonthIndex) {
    return "current";
  }
  return "upcoming";
}

export interface Exercise {
  title: string;
  instructions: string;
  timing?: string;
  format?: string;
  toolIds?: string[]; // IDs matching Tool.id in tools.ts
  facilitatorNotes?: string; // visible only in the facilitator view
}

export interface SessionSection {
  label: string;
  exercises: Exercise[];
}

export interface AgendaItem {
  title: string;
  duration: string; // e.g. "15 min", "1h", "45 min"
  subGroup?: string; // renders a sub-header above this item e.g. "4-Step Ideation"
}

export interface Session {
  day: number;
  title: string;
  description: string;
  timeRange?: string; // e.g. "9am – 5:30pm"
  subtitle?: string; // short theme label e.g. "Ideation"
  agenda?: { morning: AgendaItem[]; afternoon: AgendaItem[] };
  exercises?: Exercise[];
  sections?: SessionSection[];
  slideUrl?: string; // Drive link to the facilitator deck for this day
}

export interface ChecklistSeedItem {
  text: string;
  owner?: string;
  checked?: boolean;
}

export interface FacilitatorChecklistItem {
  category: string; // e.g. "Room & logistics", "Materials", "Tech"
  items: (string | ChecklistSeedItem)[];
}

export interface FacilitatorAgendaSeedRow {
  type: "day" | "activity" | "lunch" | "note";
  name: string;
  duration?: string;
  owner?: string;
  comment?: string;
  number?: string;
  /** Day rows only: start time, e.g. "09:00" */
  startTime?: string;
}

export interface Print {
  title: string;
  description: string;
  url?: string;
}

export interface Resource {
  title: string;
  description: string;
  url?: string;
}

export interface Module {
  id: 1 | 2 | 3 | 4;
  title: string;
  theme: string;
  location: string;
  month: string;
  outcome: string;
  welcomeMessage?: string; // shown before module content; used on Module 1 to welcome participants to the program
  before: {
    overview: string;
    preparation: string[];
    carryOver: string | null;
  };
  during: {
    sessions: Session[];
    prints: Print[];
  };
  after: {
    accomplishments: string;
    betweenModuleWork: string[];
    resources: Resource[];
    nextModulePreview: string | null;
  };
  facilitator: {
    checklist: FacilitatorChecklistItem[];
    slideUrls: { day: number; label: string; url: string }[];
    slideDecks: { label: string; url: string }[]; // previous year's decks as copy/edit base
    agenda?: FacilitatorAgendaSeedRow[]; // explicit facilitator agenda seed (overrides during.sessions)
  };
}

export const MODULES: Module[] = [
  {
    id: 1,
    title: "Challenge Scoping",
    theme: "Understanding the real problem",
    location: "Berlin",
    month: "April",
    outcome:
      "Define a well-scoped innovation challenge backed by empathy research and a shared team contract.",
    welcomeMessage:
      "Welcome to the Tesla Program — your flagship innovation leadership journey.\n\nOver the next nine months you'll work in a cross-functional team to tackle a real strategic challenge facing the energy sector. Guided by your facilitators, you'll move through four modules — from scoping the problem and talking to the people who live it, to generating bold ideas, building a business model, and pitching to senior leadership.\n\nThis is not a training course. There are no pre-packaged answers. The program is designed to put you in genuine uncertainty and equip you to navigate it — developing both the methods and the mindset that define innovation leadership.\n\nYou'll be supported throughout by facilitators, a dedicated coaching programme, and peers who are on the same journey. But the work, the choices, and the outcomes belong to your team.\n\nModule 1 starts in Berlin. You'll meet your teammates, get your challenge, and leave with a research plan. Let's begin.",
    before: {
      overview:
        "Module 1 sets the foundation for your innovation journey. Before we can design meaningful solutions, we need to deeply understand the challenge we're tackling — not the surface-level problem, but the real human and systemic tensions underneath. Come ready to question assumptions, challenge your initial brief, and listen more than you speak.",
      preparation: [],
      carryOver: null,
    },
    during: {
      sessions: [
        {
          day: 1,
          title: "Innovation Introduction & Simulation Game",
          timeRange: "9am – 5:30pm",
          subtitle: "Introduction",
          slideUrl: "/decks/module-1-day-1.pdf",
          description:
            "Day 1 is about orientation and immersion. You'll get grounded in the program, meet your fellow participants, and experience innovation in action through a full simulation game before the afternoon reveals the real challenges you'll be working on.",
          agenda: {
            morning: [
              { title: "Welcome & Energizer", duration: "15 min" },
              { title: "01. Who are we? — company slides", duration: "30 min" },
              { title: "02. Program Introduction", duration: "10 min" },
              { title: "03. Innovation Introduction", duration: "20 min" },
              { title: "04. Learning goals", duration: "1h 15 min" },
              { title: "05. Simulation Game", duration: "1h 30 min" },
            ],
            afternoon: [
              { title: "06. Pitches", duration: "30 min" },
              { title: "07. Simulation Game Retro", duration: "20 min" },
              { title: "08. Foursight — team creation", duration: "45 min" },
              { title: "09. Challenge Introduction", duration: "45 min" },
              { title: "Retro — Wrap-up", duration: "30 min" },
              { title: "10. Opening dinner", duration: "3h" },
            ],
          },
          sections: [
            {
              label: "Morning",
              exercises: [
                {
                  title: "Learning Goals",
                  timing: "1h 15 min",
                  format: "Plenary",
                  instructions:
                    "Before diving into the program content, we align on what each participant wants to get out of this experience.\n\nEach person shares:\n• What brings you to the Tesla Program?\n• What do you hope to be able to do differently by the end of it?\n• What would make this year a success for you personally?\n\nThe facilitator clusters themes and reflects back the shared ambitions of the group. These become a living reference point — you'll revisit them on Day 3 of every module.",
                },
                {
                  title: "Simulation Game",
                  timing: "1h 30 min",
                  format: "Teams",
                  instructions:
                    "A hands-on simulation that gives you a felt sense of the innovation process before you apply it to a real challenge.\n\nThe setup: 4 teams of 5 work on a real innovation challenge — the Berlin tourism case (a huge sector for the city, yet its visitor Net Promoter Score has fallen five years running). Each team has a budget of €100 and every tool costs €50, so you have to choose which tools to \"buy\" (concept card, problem validation questions, persona, customer journey, business model canvas, pitching canvas, and more — the full list is shared in Teams).\n\nThe goal is not to win — it's to notice how you think, how your team makes decisions under time pressure, and where you get stuck. It's a pressure cooker.\n\nEach team delivers a 5-minute pitch to the jury at 14:15. The jury judges on: number of interviews, customer and problem understanding, clarity of the selected problem (prioritise!), whether it's worth solving and what's in it for Berlin, problem sizing, and a first solution idea.",
                },
              ],
            },
            {
              label: "Lunch break 🍝",
              exercises: [],
            },
            {
              label: "Afternoon",
              exercises: [
                {
                  title: "Simulation Game Retro",
                  timing: "20 min",
                  format: "Plenary",
                  instructions:
                    "A short structured debrief on the simulation game — not on the solution, but on how your team worked.\n\nEach team reflects on:\n• What did you do well as a team?\n• Where did you get stuck or make a poor decision?\n• What would you do differently if you ran it again?\n\nOne insight per team, shared with the full group. These observations often predict team dynamics for the rest of the program.",
                },
                {
                  title: "Foursight — Team Creation",
                  timing: "45 min",
                  format: "Plenary",
                  instructions:
                    "Foursight is a thinking-style assessment that reveals how each person naturally contributes to an innovation process.\n\nThe four styles:\n• Clarifier — energised by understanding the problem deeply before acting\n• Ideator — energised by generating possibilities and breaking conventions\n• Developer — energised by refining and making ideas feasible\n• Implementer — energised by getting things done and making them real\n\nAfter each person learns their profile, teams are formed with complementary styles in mind. Understanding your team's Foursight mix helps you lean into strengths and anticipate tension points across the four modules.",
                },
                {
                  title: "Challenge Introduction",
                  timing: "45 min",
                  format: "Plenary",
                  instructions:
                    "The real challenges are revealed. Each challenge has been defined by your sponsors and represents a genuine strategic or operational problem the organisation is grappling with.\n\nEach sponsor has 5 minutes total — 3 minutes to present + 2 minutes for Q&A. In their 3 minutes they cover:\n• Who they are, their role within Elia, and why they're sponsoring this challenge\n• A one-sentence summary of the challenge\n• The target customer segment\n• Why this challenge matters and for whom\n• How they can support your team — what can you learn from them?\n\nParticipants listen, ask clarifying questions, and begin forming initial impressions — which challenge resonates? Where do you see the most opportunity? Tomorrow you'll go deeper.",
                },
              ],
            },
          ],
        },
        {
          day: 2,
          title: "Challenge Scoping & Design Thinking",
          timeRange: "9am – 5:30pm",
          subtitle: "Scoping",
          slideUrl: "/decks/module-1-day-2.pdf",
          description:
            "Day 2 is where you choose your challenge and begin scoping it properly. The morning moves from selection to structured scoping — including a live call with your sponsors. The afternoon introduces design thinking and gets you into the empathy research mindset with real interview practice.",
          agenda: {
            morning: [
              { title: "00. Energizer", duration: "15 min" },
              { title: "01. Challenge selection", duration: "1h" },
              { title: "02. Challenge scoping", duration: "15 min" },
              { title: "03. Practice — scoping", duration: "35 min" },
              { title: "04. Challenge scoping with sponsors 📞", duration: "1h" },
            ],
            afternoon: [
              { title: "05. Design thinking (1/2)", duration: "35 min" },
              { title: "06. Teaming on the Fly", duration: "45 min" },
              { title: "07. Design thinking (2/2)", duration: "25 min" },
              { title: "08. Practice — Interviewing (The Nest)", duration: "20 min" },
              { title: "09 & 10. Teamwork — target audience & interview prep", duration: "40 min" },
              { title: "Retro — Wrap-up", duration: "15 min" },
            ],
          },
          sections: [
            {
              label: "Morning",
              exercises: [
                {
                  title: "Challenge Selection",
                  timing: "1h",
                  format: "Plenary",
                  instructions:
                    "Each participant chooses the challenge they'll work on for the rest of the program.\n\nThe selection process:\n1. Spend 10 minutes silently reviewing the challenge briefs shared yesterday.\n2. Write your top two preferences and your reasoning for each.\n3. In a facilitated conversation, each person shares their choice and why.\n4. The facilitator balances teams across challenges to ensure no challenge is under-resourced.\n\nThis is a significant decision. Choose based on where you can have the most impact — not just what feels familiar or comfortable.",
                },
                {
                  title: "Practice — Challenge Scoping",
                  timing: "35 min",
                  format: "Breakout rooms",
                  toolIds: ["scoping-canvas"],
                  instructions:
                    "Apply the Scoping Canvas to your chosen challenge — this is a first draft, not a final answer.\n\nWork through the eight sections with your team:\n• HMW framing — your first How Might We question\n• Strategic importance — why this challenge matters\n• Customer segment — who is most affected\n• Current situation — what is happening today\n• Underlying assumptions — what you're taking for granted\n• Open questions — what you don't yet know\n• Goal / success definition — what a good outcome looks like\n• Related initiatives — what else is already in motion\n\nYou'll refine this canvas during and after the sponsor call.",
                },
                {
                  title: "Challenge Scoping with Sponsors 📞",
                  timing: "1h",
                  format: "Plenary",
                  instructions:
                    "A live call with your sponsor. Use this time to test your scoping assumptions and ask the questions your canvas left open.\n\nPrepare before the call:\n• Review your Scoping Canvas gaps — what are the three most important things you don't yet understand?\n• Prepare specific, open questions (not yes/no)\n• Assign a note-taker and a timekeeper for the call\n\nDuring the call:\n• Lead with your current understanding — 'We think the challenge is X because Y. Is that right?'\n• Probe for what success looks like from the sponsor's perspective\n• Ask about related initiatives and stakeholders they recommend you speak with\n\nAfter the call, update your Scoping Canvas immediately while the conversation is fresh.",
                },
              ],
            },
            {
              label: "Lunch break 🍝",
              exercises: [],
            },
            {
              label: "Afternoon",
              exercises: [
                {
                  title: "Design Thinking Introduction",
                  timing: "1h",
                  format: "Plenary",
                  instructions:
                    'A two-part introduction to design thinking as the methodology underpinning the Tesla Program.\n\nThe mindset: innovation should be driven by users, not technology. Every idea lives at the intersection of desirability (do people want it?), viability (does it work as a business?) and feasibility (can we build it?) — and you always start with desirability. The top reason startups fail is "no market need", so understanding people comes first.\n\nAre you solving the right problem? Business thinking jumps straight from problem to solution; design thinking inserts a step — understand the underlying need and redefine the problem before solving it (the classic "faster horses" trap).\n\nThe process: Empathise → Define → Ideate → Prototype → Test. Each module maps to a phase; today you\'re in Empathise. Three empathy research techniques, from surface to deep: Interviews (what people say), Observation (what people do), Immersion (how people feel).\n\nThe interview process has four steps — find the right people (including extreme users), prepare, conduct, and analyse. When interviewing: ask open (not leading) questions, ask about past experiences rather than hypotheticals, dig deeper with the 5 Whys, and remember the Mom Test — listen more than you pitch.',
                },
                {
                  title: "Teaming on the Fly",
                  timing: "45 min",
                  format: "Team",
                  instructions:
                    "Now that teams are set, take time to intentionally design how you'll work together.\n\nCover:\n• Decision-making — how will you resolve disagreements?\n• Communication — what channel, what cadence, what's an acceptable response time?\n• Workload — how will you divide the between-module research?\n• Strengths — what does each person bring, and how will you use that?\n• One explicit team norm — something specific you all commit to\n\nWrite it down. This is your team contract for the year. Return to it if things get difficult.",
                },
                {
                  title: "Practice — Interviewing (The Nest)",
                  timing: "20 min",
                  format: "Pairs",
                  toolIds: ["problem-validation-questions"],
                  instructions:
                    "Practise interviewing before you run the real thing. The brief: find out what the biggest problems are in balancing work and life for busy urban professionals (the \"Nest\" case, with guest Maximilian Ross).\n\n20 minutes in total:\n• 5 minutes to prepare your interview\n• 2 × 5 minutes interviewing — one leads with the Problem Interview script, the other answers, then swap\n• 5 minutes to reflect together\n\nUse the Problem Interview questions: 'Tell me about the last time you…', 'What was difficult about that experience?', 'Why was that difficult?', 'How are you solving that now — why is that not awesome?'\n\nDebrief: which questions produced rich stories, where did it feel leading, and what will you change before your real interviews?",
                },
                {
                  title: "Target Audience & Interview Script",
                  timing: "40 min",
                  format: "Team",
                  toolIds: ["problem-validation-questions", "customer-journey-map"],
                  instructions:
                    "Define who you'll interview before Module 2 and prepare your interview guide.\n\nStep 1 — Target audience: who are the people most affected by your challenge? Be specific — not 'company employees' but 'field technicians who coordinate maintenance schedules'.\n\nStep 2 — Interview script: use the Problem Validation Questions template as your starting point. Tailor the questions to your challenge and arrange them in three phases:\n• Warm-up — context-setting questions about the person and their role\n• Core — story-based questions: 'Tell me about the last time you…', 'Walk me through how you currently…'\n• Forward — 'What would better look like?', 'What have you already tried?'\n\nAssign interview roles: one person leads the conversation, one takes notes and tracks time. Record sessions where possible for transcription.",
                },
              ],
            },
          ],
        },
        {
          day: 3,
          title: "Finish Scoping, Empathy Toolset & Wrap-up",
          timeRange: "9am – 3pm",
          subtitle: "Research",
          slideUrl: "/decks/module-1-day-3.pdf",
          description:
            "Day 3 closes the module by finishing your scoping work and equipping you with the empathy research toolkit — Interview process, Segment Profile (Jobs / Pains / Gains), and Customer Journey Map — that you'll apply between modules. The afternoon wraps up with a retrospective and alumni reception.",
          agenda: {
            morning: [
              { title: "00. Energizer", duration: "15 min" },
              { title: "01. Finish Scoping & Research Plan", duration: "50 min" },
              { title: "02. Empathy toolset", duration: "45 min" },
              { title: "03. To-dos before Module 02", duration: "20 min" },
              { title: "04. Asynchronous working", duration: "20 min" },
            ],
            afternoon: [
              { title: "05. Finalize Scoping & Research Plan", duration: "30 min" },
              { title: "06. Retrospective Module 01", duration: "30 min" },
              { title: "07. Reception (alumni from 14:30)", duration: "1h" },
              { title: "Wrap-up", duration: "15 min" },
            ],
          },
          sections: [
            {
              label: "Morning",
              exercises: [
                {
                  title: "Finish Scoping & Research Plan",
                  timing: "50 min",
                  format: "Team",
                  toolIds: ["scoping-canvas", "problem-validation-questions"],
                  instructions:
                    "Use this session to complete your Scoping Canvas based on what you learned from the sponsor call yesterday.\n\nReview and fill in any remaining gaps:\n• Uncertain assumptions — what did the sponsor confirm or challenge?\n• Open questions — which ones are now answered, which need research?\n• Research plan — who will you interview and why? Be specific: name or role, how you'll reach them, and who on the team will lead each conversation.\n\nAlso review your Problem Validation Questions template. Customise the questions to your challenge before finalising. Aim to leave this session with a completed Scoping Canvas and an assigned interview list.",
                },
                {
                  title: "Empathy Toolset: Interview Process, Segment Profile & Customer Journey",
                  timing: "45 min",
                  format: "Plenary",
                  toolIds: ["problem-validation-questions", "customer-journey-map"],
                  instructions:
                    "Three tools and a process to structure your empathy research between modules.\n\n**Interview process — 4 steps:**\n1. Find the right people — prioritise people who live the problem daily, not those who manage it from a distance.\n2. Interview preparation — use the Problem Validation Questions template. Assign roles: one person leads, one takes notes and tracks time. Record sessions for transcription where possible.\n3. Conducting the interview — follow your script but chase interesting threads. The best insights often come from unexpected directions.\n4. Analysis — collect notes and transcripts, then cluster highlights per profile across Jobs-to-be-done, Pains, and Gains.\n\n**Segment Profile Canvas:**\nGroup your interview findings into 2–3 distinct user profiles. For each, capture:\n• Jobs-to-be-done — what is this person trying to accomplish?\n• Pains — what makes it difficult or frustrating?\n• Gains — what outcomes would genuinely delight them?\n\nNote: avoid demographic labels. Profiles should be defined by behaviour and need, not age or job title. Two people with the same title can have completely different Jobs, Pains, and Gains.\n\n**Customer Journey Map:**\nOnce you have your profiles, map the end-to-end experience of your primary profile across Phases, Actions, Feelings (draw the emotion curve), and Channels. Circle the moments where the emotion curve dips lowest — these pain zones become the input for problem scoring at the start of Module 2.",
                },
                {
                  title: "To-dos Before Module 02 & Asynchronous Working",
                  timing: "40 min",
                  format: "Team",
                  instructions:
                    "Two months separate this module from Brussels. Leave today with a concrete plan and the infrastructure to execute it.\n\n**Entry ticket for Module 2 (before it starts on 24 June):**\n• Conduct a minimum of 10 customer interviews — interviews with stakeholders or sponsors don't count toward the 10\n• Build validated customer profile(s) for your segment(s), each capturing Jobs-to-be-done / Pains / Gains\n• Map a Customer Journey for each profile and circle the top pain zones\n\n**Finish Module 1 now:**\n• Finish your Module 1 section in Miro\n• Plan your first meet-up with your sponsor\n• Create a Kanban board to track the between-module work\n\n**Async working setup:**\n• Shared Miro board — create it now, share the link, agree on naming conventions so anyone can navigate it\n• One communication channel for quick coordination\n• Check-in rhythm — a standing 30-minute sync every two weeks is usually enough to maintain momentum\n\n**Coaching calls — book before you leave:**\n• 20 May, 9am–2pm — team coaching with your facilitators\n• 4 June — 45-min individual slots to be scheduled",
                },
              ],
            },
            {
              label: "Lunch break 🍝",
              exercises: [],
            },
            {
              label: "Afternoon",
              exercises: [
                {
                  title: "Finalize Scoping & Research Plan",
                  timing: "30 min",
                  format: "Team",
                  toolIds: ["scoping-canvas", "problem-validation-questions"],
                  instructions:
                    "Final check before you leave: is your research plan specific enough to execute independently?\n\nFor each interview task:\n• Named owner (not 'the team')\n• Target person — name or role and how you'll reach them\n• Deadline\n\nReview your Problem Validation Questions one last time. Remove leading questions. Confirm you have at least 10 interviews distributed across team members before closing the session.",
                },
                {
                  title: "Retrospective — Module 01",
                  timing: "30 min",
                  format: "Plenary",
                  instructions:
                    "A short structured reflection on the module as a learning experience.\n\nEach participant adds three sticky notes to the shared Miro board:\n• One thing I'm taking away\n• One thing that surprised me\n• One thing I want to do differently\n\nThe facilitator clusters and reads aloud. No attribution — this is collective reflection, not evaluation.\n\nClose with one commitment per team: what will you do in the next two weeks to keep momentum from this module alive?",
                },
              ],
            },
          ],
        },
      ],
      prints: [
        {
          title: "FourSight Thinking Profile",
          description:
            "A validated self-assessment of your individual creative process preference across four stages: Clarify (defining the problem), Ideate (generating options), Develop (strengthening and refining a solution), and Implement (planning and selling the execution). Use it early in Module 1 to understand your own default thinking style and how it complements — or clashes with — your teammates'.",
        },
        {
          title: "Interview Guide",
          description:
            "Structured template for running a 45-minute empathy interview, including warm-up, context, story, and reflection phases.",
          url: "https://drive.google.com/file/d/16It8Ct5AJWYZRxzXiEPKa4yaF1h9Ryo8/view",
        },
        {
          title: "Scoping Canvas (A3)",
          description:
            "Eight-section canvas to define the challenge space before fieldwork begins — HMW framing, strategic relevance, customer segment, current situation, assumptions, open questions, and success definition.",
          url: "https://drive.google.com/file/d/1ehmWQHz_c86i2QVgoMFDz5wkXXW0N9LA/view",
        },
        {
          title: "Customer Journey Map (A1)",
          description:
            "Visualise the end-to-end experience of your customer segment across Phases, Actions, Feelings, and Channels to spot the pain points worth digging into.",
          url: "https://drive.google.com/file/d/15aUVbUMAs7PrmF7UQoAS3BavSe4fu7Qh/view",
        },
        {
          title: "Problem Sizing (A3)",
          description:
            "A quick guesstimate of the annual value of a user problem — user segment size × frequency × severity, stress-tested with PESTEL boosters and setbacks.",
          url: "https://drive.google.com/file/d/1KHBxbAlXy7gqzptPLMJWUk3R2CN3WPkN/view",
        },
        {
          title: "Problem Validation Questions (A4)",
          description:
            "A structured question bank for empathy interviews covering finding the right people, interview prep, running the session, and analysis.",
          url: "https://drive.google.com/file/d/1wCtF6xuq-8c8SEMpiWDXp_HLN2CTu0u3/view",
        },
      ],
    },
    after: {
      accomplishments:
        "You've scoped your innovation challenge, met your team, and built your research plan. You leave with a completed Scoping Canvas, a validated interview guide, and a clear picture of who you need to talk to before Brussels.",
      betweenModuleWork: [
        "Finish Module 1 section in Miro",
        "Plan meetup with your sponsor",
        "Create a Kanban Board",
        "Minimum of 10 customer interviews (stakeholders and sponsors do not count)",
        "Validated customer profile(s) for your customer segment(s)",
        "Customer Journey(s) for your customer profile(s)",
      ],
      resources: [
        {
          title: "Problem Validation Questions",
          description:
            "Structured question bank for empathy interviews — use it to prepare and run your 10 customer interviews.",
          url: "https://drive.google.com/file/d/1wCtF6xuq-8c8SEMpiWDXp_HLN2CTu0u3/view",
        },
        {
          title: "Customer Journey Map",
          description:
            "Use this tool to map the experience of your validated customer profile across phases, actions, feelings, and channels.",
          url: "https://drive.google.com/file/d/15aUVbUMAs7PrmF7UQoAS3BavSe4fu7Qh/view",
        },
        {
          title: "IDEO Human-Centered Design Toolkit",
          description:
            "Field guide for empathy research and insight synthesis. Chapters 1–3 are most relevant for this inter-module period.",
          url: "https://www.designkit.org/resources/1.html",
        },
      ],
      nextModulePreview:
        "Module 2 in Brussels picks up where your research leaves off. You'll bring your insights into creative problem-solving sessions, generate and develop solution concepts, and build your first rough prototype to test with real users.",
    },
    facilitator: {
      checklist: [
        {
          category: "Room & logistics",
          items: [
            "Book workshop room for 3 days (capacity ≥ 20 pax, movable furniture)",
            "Confirm catering: coffee/tea on arrival, lunch Day 1–3, afternoon break",
            "Print name badges and table tent cards",
            "Prepare welcome packs (program overview, team assignment sheet)",
          ],
        },
        {
          category: "Materials to print",
          items: [
            "Scoping Canvas — 1× A3 per team (5 teams)",
            "Customer Journey Map — 1× A1 per team",
            "Problem Sizing — 1× A3 per team",
            "Problem Validation Questions — 1× A4 per participant",
            "Team Contract template — 1× A4 per team",
            "Post-its (3 colours), markers, dot stickers for voting",
          ],
        },
        {
          category: "Tech & tools",
          items: [
            "Set up shared Miro board — one board per team, linked from the master board",
            "Test projector / screen in main room",
            "Confirm Teams/Zoom link for remote sponsor calls",
            "Share Miro board links with participants 48h before Day 1",
          ],
        },
        {
          category: "Entry ticket check",
          items: [
            "Confirm all participants have read the Tesla Program brief",
            "Confirm sponsor availability for Day 2 challenge reveal call",
            "Confirm coaching call slots booked: 20 May (team) + 4 June (individual)",
          ],
        },
      ],
      slideUrls: [
        {
          day: 1,
          label: "Day 1 — Introduction & Simulation",
          url: "/decks/module-1-day-1.pdf",
        },
        {
          day: 2,
          label: "Day 2 — Challenge Scoping",
          url: "/decks/module-1-day-2.pdf",
        },
        {
          day: 3,
          label: "Day 3 — Research Planning",
          url: "/decks/module-1-day-3.pdf",
        },
      ],
      slideDecks: [
        {
          label: "Day 1 — 2024 deck",
          url: "https://docs.google.com/presentation/d/1ddgSPkUHTrPZ43WhMHqrODWEvcqgV_oObx-nnE3Le2o/edit",
        },
        {
          label: "Day 2 — 2024 deck",
          url: "https://drive.google.com/open?id=1_IYDwTr03ATrBoZltD7e8JEMsbo6GDZgfTnFdUCmBAU",
        },
        {
          label: "Day 3 — 2024 deck",
          url: "https://docs.google.com/presentation/d/1n-2EnnRq-G3Su1hli1m1ADgjg5pCRNzbJzazOO2uWLo/edit",
        },
      ],
    },
  },
  {
    id: 2,
    title: "Solution Ideation",
    theme: "From insight to concept",
    location: "Brussels",
    month: "June",
    outcome:
      "Generate a prioritised set of solution concepts grounded in user insights, with one concept developed to prototype-ready fidelity.",
    before: {
      overview:
        "Module 2 is where the real creative work begins. You've spent weeks living with your challenge — now it's time to generate bold ideas, develop the most promising ones, and stress-test them with early prototypes. Come with energy, a willingness to be wrong quickly, and all the insights you've gathered since Berlin.",
      preparation: [
        "Finish Module 1 section in Miro",
        "Plan meetup with your sponsor",
        "Create a Kanban Board",
        "Minimum of 10 customer interviews (stakeholders and sponsors do not count)",
        "Validated customer profile(s) for your customer segment(s)",
        "Customer Journey(s) for your customer profile(s)",
      ],
      carryOver:
        "Your empathy research synthesis, Point of View statement, and Learning Objectives Board from Module 1 will be the starting point for Day 1 ideation. Upload everything to Miro at least 48 hours before the module.",
    },
    during: {
      sessions: [
        {
          day: 1,
          title: "Share-out, Problem Scoring & HMW Framing + 4-Step Ideation",
          timeRange: "9am – 5:30pm",
          subtitle: "Ideation",
          slideUrl: "/decks/module-2-day-1.pdf",
          agenda: {
            morning: [
              { title: "Intro & Check-in", duration: "15 min" },
              { title: "Webmaster", duration: "1h" },
              { title: "Team Feedback round", duration: "30 min" },
              { title: "Customer journey share-out", duration: "20 min" },
              { title: "Problem Scoring", duration: "55 min" },
            ],
            afternoon: [
              { title: "Introduction on HMW", duration: "10 min" },
              { title: "Creating HMW", duration: "50 min" },
              { title: "Introduction on Ideation", duration: "30 min" },
              { title: "Intro on 4-Step Sketch", duration: "10 min" },
              { title: "Step 1 — Notes", duration: "10 min", subGroup: "4-Step Ideation" },
              { title: "Step 2 — Ideas & Analogy thinking", duration: "40 min" },
              { title: "Step 3 — Crazy 8s", duration: "10 min" },
              { title: "Step 3.5 — Brainstorm wall stress test", duration: "40 min" },
              { title: "Step 4 — Concept card", duration: "30 min" },
              { title: "Wrap-up", duration: "15 min" },
            ],
          },
          description:
            "Day 1 runs 9am–5:30pm. The morning opens with each team sharing their customer research findings, then runs a structured problem scoring exercise. After lunch, the focus shifts to HMW framing, an introduction to ideation principles, and a full 4-step ideation process ending with individual concept cards uploaded to Miro.",
          sections: [
            {
              label: "Morning",
              exercises: [
                {
                  title: "Share-out: Customer Research",
                  timing: "30 min",
                  format: "Plenary",
                  toolIds: ["customer-journey-map"],
                  instructions:
                    "Present your customer research to the group as a learning pitch opportunity. Cover:\n• Who is your customer segment?\n• What are their 3 greatest pain points?\n• You may use your customer journey map to illustrate.\n\nThe goal is to transfer insight efficiently — keep it focused. Each team gets a few minutes.",
                },
                {
                  title: "Problem Scoring",
                  timing: "40 min",
                  format: "Breakout rooms",
                  toolIds: ["problem-sizing"],
                  instructions:
                    "Goal: Prioritise customer problems based on their impact and feasibility to address.\n\nStep 1 — Cluster your insights on Miro by theme (if not already done).\nStep 2 — Score each problem cluster: How painful is this for the customer? How feasible is it to address?\n\nTip: Think like the 'slow elevator problem' — the key to solving a problem is often defining it correctly in the first place. Reframe 'the elevator is too slow' to 'I'm bored waiting' and suddenly the solution space opens up.\n\nYou have 40 minutes total. Work in your break-out room.",
                },
                {
                  title: "How Might We (HMW) Framing",
                  timing: "45 min",
                  format: "Breakout rooms",
                  toolIds: ["hmw-statement-builder"],
                  instructions:
                    "Transform your top-scored problem into a How Might We question.\n\nHMW formula: 'How might we [action + subject] for [user persona] so that [outcome]?'\n\nRules:\n• Focus on customer needs, pains, and problems\n• Allow for brainstorming — not too broad, not too narrow\n• Never mention a specific solution in the HMW\n• It takes several iterations to get it right\n\nExample: 'How might we streamline our customer order processing to reduce processing time 10x?'\n\nAim for at least 3 strong HMWs per team before selecting your top one.",
                },
              ],
            },
            {
              label: "Lunch break 🍝",
              exercises: [],
            },
            {
              label: "Afternoon — 4-Step Ideation Process",
              exercises: [
                {
                  title: "Step 1: Notes",
                  timing: "10 min",
                  format: "Individual",
                  instructions:
                    "Open your Miro board for the last time. Review all key information and write down the essentials on A4 paper — this is for your eyes only, so don't worry about neatness.\n\nSuggested things to capture:\n• Your team's HMW questions\n• Assumptions or open questions you have\n• What success looks like for your customer\n• Any extra considerations or sparks from the research\n\nWork alone ('together alone') with pen and paper.",
                },
                {
                  title: "Step 2: Ideas via Analogy Thinking",
                  timing: "30 min",
                  format: "Hybrid",
                  instructions:
                    "Use Microsoft Copilot as a provocateur to break out of familiar thinking patterns.\n\n1. Pick one ideation prompt and paste your team's HMW into the placeholder.\n2. You're not looking for a ready-made solution — you're looking for one word, mechanism, or logic you can steal and apply to your challenge. Write that idea on a post-it.\n3. Try at least two different thinking modes before moving on.\n\nAvailable prompt types:\n• Steal from a different industry — how would Netflix, Amazon, or Spotify solve this?\n• Nature as the engineer — how would a forest, ant colony, or immune system solve it?\n• Flip it — how would you make the problem dramatically worse? Then reverse each failure.\n• 10x the ambition — what if the solution had to be 10x better, not 10% better?\n• The radical newcomer — a well-funded startup with no legacy constraints: what do they build?\n• The citizen's lens — what would a household or small business owner want?\n\nAfter 30 seconds per post-it, share your sparks with the team.",
                },
                {
                  title: "Step 3: Crazy 8s",
                  timing: "8 min",
                  format: "Hybrid",
                  instructions:
                    "Rapidly sketch eight different ideas — one per block.\n\n1. Take an A4 sheet and fold it into 8 equal blocks.\n2. Before the timer: take 30 seconds to glance at the post-its on the team wall.\n3. When the timer starts: 30 seconds to write the core idea, 30 seconds to sketch it. Move to the next block when time is called — even if you're not done.\n4. Each block should explore a different angle or dimension of a potential solution.\n\nRules:\n• Go for hybrid brainstorming\n• Defer judgment — there are no bad ideas\n• Go for quantity over quality\n• Capture everything\n\nThe rapid pace is not a bug — it stops your inner critic from taking over.",
                },
                {
                  title: "Step 3.5: Brainstorm Wall Stress Test",
                  timing: "30 min",
                  format: "Team",
                  instructions:
                    "Challenge your ideas against two hard questions before moving forward: does it create real value, and will it still matter in 10 years?\n\n1. Put your ideas on the wall — each person picks 1–2 most compelling ideas and writes each on a post-it in one sentence. You should have 6–10 ideas visible as a team.\n2. Pick the first idea: the author reads it aloud in one sentence. No presentation, no context — just the idea.\n3. Value challenge: one facilitator fires the Value challenge questions to stress-test the idea. Anyone can answer; the author listens first and responds last.\n   • Who benefits from this and what do they actually get?\n   • What does the organisation gain: money, trust, efficiency, position?\n   • Is this a genuinely new solution or an existing thing made slightly better?\n4. Ambition challenge: same facilitator fires the Ambition challenge questions.\n   • Does this idea get stronger or weaker as AI becomes more embedded in how the organisation works?\n   • If the problem this solves disappeared tomorrow, does the idea disappear with it?\n   • Will this still matter in 10 years?\n5. Call it: the facilitator calls a verdict — keep, sharpen, or drop. The author speaks last.\n6. Lock your shortlist: pick your top ideas and write them in the amber block.",
                },
                {
                  title: "Step 4: Concept Card",
                  timing: "30 min",
                  format: "Individual",
                  toolIds: ["concept-card"],
                  instructions:
                    "Translate your best idea into a single, self-contained concept that someone else can understand and present — without you in the room.\n\n1. Pick your idea: look at everything on the wall, your Crazy 8, the shortlist. Pick the one you believe in most.\n2. Fill in the concept card: work through each section individually and in silence. Design for the reader — every section should make sense to someone who was not in the room.\n3. Name it: give your concept a title that captures what it does and makes someone want to know more. Avoid generic names like 'smart platform' or 'digital solution'.\n4. Photograph and upload: take a photo of your completed card and place it on the Miro board in the designated space. Keep it anonymous — no names on the card or in the file name.\n\nTips:\n• Clarity over beauty — rough sketches are fine\n• Anonymous by design — the idea gets judged on its merit, not who made it\n• Variety is the point — ten different directions beat ten versions of the same safe idea",
                },
              ],
            },
          ],
        },
        {
          day: 2,
          title: "Prioritisation — Museum Walk, Concept Decision, Storyboard & Assumption Mapping",
          timeRange: "9am – 5:30pm",
          subtitle: "Prioritisation",
          slideUrl: "/decks/module-2-day-2.pdf",
          agenda: {
            morning: [
              { title: "Energiser", duration: "15 min" },
              { title: "Introduction of Day 2", duration: "10 min" },
              { title: "Museum walk + Heat Mapping", duration: "30 min" },
              { title: "Final choice", duration: "40 min" },
              { title: "Create storyboard", duration: "45 min" },
              { title: "Prep Alumni lunch", duration: "15 min" },
            ],
            afternoon: [
              { title: "Core Quadrants", duration: "1h" },
              { title: "Final concept card", duration: "30 min" },
              { title: "Puzzle", duration: "45 min" },
              { title: "Assumption Mapping", duration: "1h 20 min" },
              { title: "Wrap-up", duration: "20 min" },
            ],
          },
          description:
            "Day 2 runs 9am–5:30pm. Goal: end the day with one concept prioritised per team and all remaining assumptions mapped. The morning moves through silent voting on concept cards, a structured final decision, and a 45-minute AI-assisted storyboard. After an alumni lunch, the afternoon finalises the team concept card and closes with a 1-hour assumption mapping session.",
          sections: [
            {
              label: "Morning",
              exercises: [
                {
                  title: "Museum Walk & Heat Mapping",
                  timing: "30 min",
                  format: "Silent voting",
                  instructions:
                    "Each team member places their concept card on the wall.\n\n1. Spend 5 minutes silently reading all the concept cards — no talking allowed.\n2. Each person gets 3 sticky dot votes. Place dots on the elements you like across any of the concept cards.\n3. Continue for 5 minutes per concept card (30 min total).\n\nThe dot clusters that emerge show where the group's energy is concentrated, independent of who made what. This surfaces the most popular elements for further development.",
                },
                {
                  title: "Final Decision — Pick Your Concept",
                  timing: "30 min",
                  format: "Team",
                  instructions:
                    "Review the winning ideas from the museum walk.\n\nIf beneficial, combine elements from different concept cards to create a stronger unified solution — but beware the 'Frankenstein' trap: a concept stitched together from too many parts becomes incoherent. Test it: can you explain what this is, what it's for, and why someone should care in 8 seconds? If not, it's too complex.\n\nEnsure all team members are aligned with the final decision before moving forward.\n\nAs a check, place your ideas on the How-Now-Wow matrix:\n• How: Difficult to implement, high innovation\n• Now: Easy to implement, incremental improvement\n• Wow: Easy to implement, high innovation → aim for this quadrant\n\nTake a photo of your How-Now-Wow Matrix and upload to Miro.",
                },
                {
                  title: "Storyboard",
                  timing: "45 min",
                  format: "Team",
                  toolIds: ["storyboard"],
                  instructions:
                    "Bring the winning concept to life as a user journey — the goal is to surface anything vague, assumed, or missing before you go further.\n\nStep 1 — Set the scene: agree as a team and write at the top of the sheet who the user is, what their situation is before encountering your concept, and what success looks like for them at the end.\n\nStep 2 — Map the frames: talk through the full journey out loud before anyone draws. Agree on what happens in each frame, then pick up a pen.\n\nStep 3 — Draw it: rotate who draws each frame. Stick figures are enough. Add a one-line caption under each frame.\n\nStep 4 — AI pressure test: after each frame, the designated Copilot user runs the challenger prompt in your Miro board:\n'Here is one frame of a storyboard for the following concept: [paste concept]. The frame shows: [describe it]. What is unclear, assumed, or missing? What would a sceptical stakeholder ask?'\nRead the output aloud. If it surfaces a gap the team cannot answer, add a question-mark post-it to that frame and keep drawing.\n\nUpload to Miro: individual concept cards + completed storyboard.",
                },
              ],
            },
            {
              label: "Alumni lunch 🍝",
              exercises: [],
            },
            {
              label: "Afternoon",
              exercises: [
                {
                  title: "Final Concept Card",
                  timing: "30 min",
                  format: "Team",
                  toolIds: ["concept-card"],
                  instructions:
                    "As a team, finalise your solution concept card.\n\n1. Collectively fill in each section of the concept card — add sticky notes for context and explanations where needed.\n2. The concept card should speak for itself without requiring additional explanation.\n3. Give the final concept a catchy, specific title. Avoid generic names like 'smart platform'. Your concept must be clear in 8 seconds: what is this? What is it for? Why should I care?\n4. Take a photo and upload the final concept card to Miro.\n\nRemember: no Frankenstein — a clear, focused concept beats a stitched-together one every time.",
                },
                {
                  title: "Assumption Mapping",
                  timing: "1 hour",
                  format: "Team",
                  toolIds: ["assumption-mapper"],
                  instructions:
                    "Prioritise the key assumptions behind your final concept to determine which need to be tested first.\n\nThere are 4 categories of assumptions:\n• Client/User focus (Desirability) — assumptions about underlying needs or unmet desires\n• Business Model (Viability) — assumptions about revenue, willingness to pay, scale, fit with company strategy\n• Technology (Feasibility) — assumptions about whether the idea can be built and truly adds value\n• Corporate Strategy fit — assumptions about internal alignment and deployment context\n\nStep 1 — Write your assumptions individually and in silence. One assumption per post-it. Use different coloured post-its per category.\n\nStep 2 — Read your assumptions aloud one by one. No debate — just get everything visible.\n\nStep 3 — Run the AI-challenger prompt in your Miro board to surface assumptions you may have missed. Add any new ones as fresh post-its.\n\nStep 4 — Place assumptions on the matrix (Impact × Known/Unknown). For each: if this is wrong, does the concept fail? Is there hard evidence for it, or is it a guess?\n\nStep 5 — Pick your critical 2–4 assumptions in the 'important + unknown' quadrant. These are what you need to validate first.\n\nStep 6 — Photograph and upload to Miro.",
                },
              ],
            },
          ],
        },
        {
          day: 3,
          title: "Experimentation — Design Experiments, Prep Interviews & Plan Ahead",
          timeRange: "9am – 3pm",
          subtitle: "Experimentation",
          slideUrl: "/decks/module-2-day-3.pdf",
          agenda: {
            morning: [
              { title: "Feedback walk", duration: "1h" },
              { title: "Introduction of Day 3", duration: "10 min" },
              { title: "Introduction on Experimentation", duration: "30 min" },
              { title: "Design your experiment (picker)", duration: "40 min" },
              { title: "Design your experiment (card)", duration: "45 min" },
              { title: "Plan coaching session", duration: "10 min" },
            ],
            afternoon: [
              { title: "Prep interview", duration: "1h" },
              { title: "Looking back and ahead", duration: "10 min" },
              { title: "Wrap-up", duration: "20 min" },
              { title: "Fun last activity", duration: "30 min" },
            ],
          },
          description:
            "Day 3 runs 9am–3pm. Goal: leave with a clear experiment plan per team and an interview script ready to run. The morning covers validation theory and experiment design. After lunch, teams prepare interview scripts and plan ahead for Module 3.",
          sections: [
            {
              label: "Morning",
              exercises: [
                {
                  title: "Experimentation Introduction: The De-Risked Approach",
                  toolIds: ["validation-guide"],
                  instructions:
                    "Validation: the iterative process of gathering evidence and learnings around business ideas through experimentation and user testing, in order to make faster, informed, de-risked decisions.\n\nKey idea: help reduce risk and uncertainty as early and as cheaply as possible.\n\nTwo stories to anchor the thinking:\n\n• Zappos (Concierge MVP): Before building inventory or an e-commerce system, the founder took photos of local shoe store stock, posted them online, then bought and shipped any orders by hand. It tested one assumption: will people buy shoes online without trying them on? The answer validated the whole business.\n\n• Dropbox (Explainer Video MVP): In 2007, the team made a 3-minute demo video of a product that didn't yet exist, targeted at early tech adopters. The waiting list went from 5,000 to 75,000 overnight — proving demand before a single line of code was written.\n\nThe lesson: instead of building everything and releasing a full product (high risk), use shorter build-measure-learn cycles — MVP → Beta → v1.0 → v1.1 — and fail as early and cheaply as possible.\n\nAsk: not 'What will we build?' but 'What do we want to learn?'",
                },
                {
                  title: "Pick Your Experiments",
                  timing: "40 min",
                  format: "Breakout rooms",
                  toolIds: ["experiment-picker"],
                  instructions:
                    "Choose experiments to test and validate the 2–4 key assumptions from your assumption mapping.\n\nSteps:\n1. Review your critical assumptions (important + unknown quadrant from Day 2).\n2. Open the Experiment Picker and Validation Guide on Miro.\n3. Don't default to the suggested path — read through the different experiment types and pitch a few that feel interesting to your team.\n4. Choose at least: one interview experiment + one behavioral experiment.\n5. Write down the chosen experiment for each assumption.\n\nExperiment type reference (18 types):\n• Exploratory: Interviews, Focus groups, Generative sessions → easy, low-cost, deep qualitative insights\n• Conceptual: App mockups, Wireframes, Sorting cards, Explainer videos → usability and design feedback\n• Behavioral: Landing pages, A/B testing, Ad campaigns → measures real user actions, reduces bias\n• Market: Crowdfunding, Pre-sales, Letters of intent → strong market indicators, financial commitment\n• Functional: Physical prototypes, Wizard of Oz → real-world testing, comprehensive check",
                },
                {
                  title: "Create Your Experiment Cards",
                  timing: "40 min",
                  format: "Breakout rooms",
                  toolIds: ["experiment-card"],
                  instructions:
                    "Plan and design the experiments you've chosen — move from assumptions to validated learning.\n\nFor each chosen experiment, fill in an Experiment Card in Miro:\n1. Translate the assumption into a hypothesis (if [condition], then [outcome]).\n2. Define your success criteria — what result would confirm or disprove the assumption?\n3. Design a bias-free experiment — how will you run it without leading the participant?\n4. Create a checklist or to-do list with team member assignments.\n\nGuidelines:\n• Keep it simple\n• Minimal cost, maximal learning\n• Done is better than perfect\n• Iterate based on what you learn\n\nUpload your Experiment Cards to Miro before lunch.",
                },
              ],
            },
            {
              label: "Lunch break 🍝",
              exercises: [],
            },
            {
              label: "Afternoon",
              exercises: [
                {
                  title: "Prepare Your Interview Script",
                  timing: "1 hour",
                  format: "Breakout rooms",
                  instructions:
                    "Prepare an interview script ready to run after the module — this saves time during the inter-module period.\n\nSteps:\n1. Go to Miro and open the Solution Validation Interview Template.\n2. Open your Experiment Card for the interview experiment.\n3. Review the key objectives of your interview.\n4. Review who you will interview (target users, stakeholders — not sponsors).\n5. Draft relevant questions using the template. Ensure questions are:\n   • Clear and specific\n   • Unbiased (no leading questions)\n   • Aligned with your hypothesis and success criteria\n6. Assign team members to roles: interviewer, note-taker, observer.\n7. Schedule the interviews before the 29 July coaching call.\n\nWork in your break-out room.",
                },
                {
                  title: "Looking Back & Ahead — Entry Ticket for Module 3",
                  timing: "10 min",
                  format: "Team",
                  instructions:
                    "Close the module by locking in what happens before Module 3 (Business Modelling, 9–11 September).\n\nBefore the end of Module 2:\n• Send your concept card and storyboard to your sponsor\n• Share quick feedback on the sponsor collaboration — what's going well, what could be better\n\nEntry ticket for Module 3:\n• Test your key assumptions — run at least 10 solution interviews plus one behavioural experiment\n• Arrive with a validated solution concept\n• Make sure your sponsor is aligned\n\nBook your between-module coaching before you leave: a Board of Innovation session (with Sofie & Dome) and a Beanmachine session (with Pieter).",
                },
              ],
            },
          ],
        },
      ],
      prints: [
        {
          title: "All Module 2 Tool Prints",
          description:
            "Access the full set of printed templates and tool prints for Module 2 — Concept Cards, Crazy 8s, HMW Worksheets, Storyboard, Assumption Mapping, and Experiment Cards.",
          url: "https://drive.google.com/drive/folders/1LRempnlWTEw9HMmKwLdlwsP6DVQENayI",
        },
        {
          title: "Concept Card Template (A3)",
          description:
            "Printed A3 template for Step 4 of the ideation process. Fill in individually and in silence — designed so anyone can understand your concept without you in the room.",
          url: "https://drive.google.com/file/d/1ch67Axt0wc-FwjAnaXxU3Ddm2IoL_HX_/view",
        },
        {
          title: "Crazy 8s Sheet",
          description:
            "A4 sheet divided into 8 blocks for the Crazy 8s rapid sketching exercise. 1 minute per block — write the core idea and sketch it.",
          url: "https://www.sessionlab.com/methods/four-step-sketch",
        },
        {
          title: "HMW Question Worksheet",
          description:
            "Structured worksheet for generating and refining How Might We questions from your top-scored problems. Formula: How might we [action + subject] for [user] so that [outcome]?",
          url: "https://drive.google.com/file/d/1fQWyB21IUhJq4w8RApg2ulAJ3FQPZbSd/view",
        },
        {
          title: "Feedback Frame: OFNR",
          description:
            "A Nonviolent Communication feedback structure with four steps: Observation (state what you saw or heard, free of judgment), Feeling (name the emotion it triggered), Need (surface the underlying need behind that feeling), and Request (make a concrete, actionable ask). Used during prototype and concept reviews to give honest feedback without putting the other person on the defensive.",
        },
        {
          title: "Ofman Core Quadrant (A3)",
          description:
            "A self-reflection framework mapping four connected traits: Core Quality (a strength others recognise in you), Pitfall (that same quality taken too far), Allergy (the opposite trait that irritates you in others), and Challenge (the positive opposite you could grow toward). Used to reflect on personal working style and defuse team friction during the feedback walk.",
          url: "https://drive.google.com/file/d/1oS6pWIUmrbBmNG37bLyNgkszVkoCGw-g/view",
        },
        {
          title: "Storyboard (A4)",
          description:
            "An 8-frame grid for visualising the user journey with your concept scene by scene — reveals gaps and assumptions invisible in a concept card.",
          url: "https://drive.google.com/file/d/1JB5GEA17-VXYT1Ckn_QmU_Vzr4HI3taF/view",
        },
        {
          title: "Assumption Mapper (A1)",
          description:
            "A 2×2 matrix plotting your concept's assumptions by importance and testability — start with what's important and easy to test.",
          url: "https://drive.google.com/file/d/1um4EYy6CJeEaXxTzB9e8VIfwjktIcn-m/view",
        },
        {
          title: "Experiment Picker (A1)",
          description:
            "A decision-tree flowchart across 24 experiment types — exploratory, conceptual, behavioral, market, and functional — to find the right experiment for your stage.",
          url: "https://drive.google.com/file/d/18-49lXntyXAJ1aEdhr3xqb6xR48ADFUd/view",
        },
        {
          title: "Experiment Card (A4)",
          description:
            "Design a single lean experiment: type, what's being tested and how, target audience, success criteria, and timeline. One card per assumption.",
          url: "https://drive.google.com/file/d/1CFOwi7rBIM2OB8UOoPlk2qha26xDLLA_/view",
        },
        {
          title: "Validation Guide (A4)",
          description:
            "Reference guide to all 24 experiment types in the Experiment Picker — when to use each, how to run it, and common pitfalls.",
          url: "https://drive.google.com/file/d/1xr6hLtk_0296lIkqqsMUvtY7tKNoQ4mY/view",
        },
      ],
    },
    after: {
      accomplishments:
        "You've transformed your research insights into a prioritised set of solution concepts and developed your strongest idea to prototype-ready fidelity. You leave with a Concept Card, a scored problem framing, and early validation signals from real users.",
      betweenModuleWork: [
        "Test your key assumptions — minimum 10 solution interviews + a behavioral experiment",
        "Validated solution concept",
        "Sponsor aligned",
      ],
      resources: [
        {
          title: "Experiment Picker",
          description:
            "Use this to choose the right experiment type for each assumption you need to test.",
          url: "https://drive.google.com/file/d/18-49lXntyXAJ1aEdhr3xqb6xR48ADFUd/view",
        },
        {
          title: "Experiment Card",
          description:
            "Fill one card per assumption — define your hypothesis, success criteria, and to-do list before running each experiment.",
          url: "https://drive.google.com/file/d/1CFOwi7rBIM2OB8UOoPlk2qha26xDLLA_/view",
        },
        {
          title: "Validation Guide",
          description:
            "Reference guide for all 24 experiment types — descriptions, when to use each, and what good results look like.",
          url: "https://drive.google.com/file/d/1xr6hLtk_0296lIkqqsMUvtY7tKNoQ4mY/view",
        },
      ],
      nextModulePreview:
        "Module 3 in Berlin (September) shifts focus from validated concept to business viability. You'll assess the viability of your business model, map out stakeholders and value exchanges to visualise your business structure, and calculate potential user base, time saved, and revenue or cost impact.",
    },
    facilitator: {
      checklist: [
        {
          category: "Room & logistics",
          items: [
            "Book workshop room for 2 days (capacity ≥ 20 pax, wall space for posting)",
            "Confirm catering: coffee/tea on arrival, lunch Day 1–2, afternoon break",
            "Prepare sticky-dot voting sets per team",
          ],
        },
        {
          category: "Materials to print",
          items: [
            "HMW Statement Builder — 1× A4 per team",
            "Concept Card — 3× A3 per participant (participants generate multiple concepts)",
            "Storyboard — 1× A4 per participant",
            "Assumption Mapper — 1× A1 per team",
            "Experiment Picker — 1× A1 per team",
            "Experiment Card — 3× A4 per team",
            "Post-its (4 colours), markers, dot stickers",
          ],
        },
        {
          category: "Tech & tools",
          items: [
            "Verify all team Miro boards are updated with Module 1 research outputs",
            "Set up AI challenger prompt in shared doc for concept stress-testing",
            "Test projector / screen",
          ],
        },
        {
          category: "Entry ticket check",
          items: [
            "Confirm each team has completed ≥ 10 customer interviews",
            "Confirm validated customer profile(s) uploaded to Miro",
            "Confirm Customer Journey Map uploaded to Miro",
            "Confirm Module 1 Miro section complete",
          ],
        },
      ],
      slideUrls: [
        {
          day: 1,
          label: "Day 1 — Ideation",
          url: "/decks/module-2-day-1.pdf",
        },
        {
          day: 2,
          label: "Day 2 — Prioritisation",
          url: "/decks/module-2-day-2.pdf",
        },
        {
          day: 3,
          label: "Day 3 — Experimentation",
          url: "/decks/module-2-day-3.pdf",
        },
      ],
      slideDecks: [
        {
          label: "Day 1 — 2024 deck",
          url: "https://drive.google.com/open?id=1WnoL1iEAlB5Byj3i9YlmF1XdfaG0SJWg1gwWY8Psj44",
        },
        {
          label: "Day 2 — 2024 deck",
          url: "https://drive.google.com/open?id=1OuLiDnkiysHmp1N1KVxmIRxotpavoBSGq4bcVMA4jYs",
        },
        {
          label: "Day 3 — 2024 deck",
          url: "https://drive.google.com/open?id=1pCuUbEwk8WB4-uzTCVDeQRKCFJ1cEvqzLxyRK1KFLTE",
        },
      ],
    },
  },
  {
    id: 3,
    title: "Business Modelling",
    theme: "Building the case for viability",
    location: "Berlin",
    month: "September",
    outcome:
      "A business model you can defend, ballpark figures that show the value of your solution, a prioritised set of critical assumptions, and a validation roadmap to execute before Module 4.",
    before: {
      overview:
        "Module 3 is where innovation meets business reality. Great ideas fail when their underlying business logic doesn't hold. This module equips you with the tools to design, stress-test, and refine the business model around your concept — before you invest more in building it out. Come with your refined concept and an open mind about what the right business model might look like.",
      preparation: [
        "Test your key assumptions — minimum 10 solution interviews + a behavioral experiment",
        "Validated solution concept",
        "Sponsor aligned",
      ],
      carryOver:
        "Your refined Concept Card, updated Assumption Map, and prototype test findings from Module 2 are the inputs for Module 3 — the Concept Card is the direct input to the Vision Card on Day 1. Have them accessible and up to date in your shared workspace.",
    },
    during: {
      sessions: [
        {
          day: 1,
          title: "Business Model Innovation",
          timeRange: "9am – 5:30pm",
          subtitle: "Business Model Innovation",
          slideUrl: "/decks/module-3-day-1.pdf",
          description:
            "Day 1 builds the business model from the ground up. The morning opens the module, sets psychological safety, hears each team's share-out from Module 2, and introduces business model innovation before teams build their first model with the Vision Card and the Business Model Kit. The afternoon stress-tests that model through iteration, assumption mapping, a peer challenge round and a feedback-driven update — closing with a team dinner.",
          agenda: {
            morning: [
              { title: "Psychological safety", duration: "60 min" },
              { title: "Share-out", duration: "35 min" },
              { title: "Intro business model innovation (BMI)", duration: "30 min" },
              { title: "Create your business model", duration: "45 min" },
            ],
            afternoon: [
              { title: "Batteries", duration: "50 min" },
              { title: "Business model iterations & selection", duration: "70 min" },
              { title: "Business model assumptions", duration: "45 min" },
              { title: "Challenge the other team", duration: "45 min" },
              { title: "Update your business model", duration: "30 min" },
              { title: "Dinner", duration: "2h" },
            ],
          },
          sections: [
            {
              label: "Morning",
              exercises: [
                {
                  title: "Module 03 Introduction",
                  format: "Plenary",
                  instructions:
                    "Where we are in the programme: Module 01 was scoping, Module 02 was design thinking — empathise, define, ideate, prototype and test. Module 03 is business modelling. Module 04 is the pitches.\n\nYour entry ticket to Module 03 was:\n1. Test your key assumptions — at least 10 solution interviews plus a behavioural experiment\n2. A validated solution concept\n3. Sponsor aligned\n\nThe goals of Module 03:\n1. Align as a team and progress with your project\n2. Learn new Business Model Innovation tools\n3. Create a roadmap for how to test your identified assumptions and collect the proof points for your Module 04 pitch\n\nHouse rules for the three days:\n• Time — be respectful of everyone's time. Arrive on time for sessions; if you have an urgent call or a meeting you can't move, tell us in advance.\n• Out of office — you should have set an out-of-office message so you can be fully engaged across the three days.\n• Engage — the more you engage, the more you gain. For main-room activities we'll ask for a notetaker so we're not all on our laptops unnecessarily.",
                },
                {
                  title: "Psychological Safety",
                  timing: "60 min",
                  format: "Plenary",
                  instructions:
                    "Psychological safety is the foundation of honest, high-performing teams — and it's a prerequisite for the kind of creative risk-taking Module 3 demands.\n\nKey principle: team members feel safe to speak up, take risks, and be wrong without fear of embarrassment or punishment. Amy Edmondson's research shows it's the single strongest predictor of team effectiveness.\n\nAs a team, reflect on:\n• Do people say what they really think in our team, or what they think others want to hear?\n• When someone is wrong, how does the team respond?\n• Who speaks least in our sessions? Why?\n\nOne collective commitment: agree on one concrete behaviour the team will practise this module to raise psychological safety.",
                },
                {
                  title: "Share-out",
                  timing: "35 min",
                  format: "Plenary",
                  instructions:
                    "Each team shares their Module 02 work with the full group.\n\nYou have:\n• 15 min to prepare as a team\n• 5 min to share out per team\n\nShare your problem, solution, feedback and learnings — did you (in)validate your key assumptions?\n\nThe format is up to you. No need for a deck. Lead with what changed: what did you set out to test, what did you find, and how has your thinking shifted since Brussels?\n\nLet us hear your story.",
                },
                {
                  title: "Tension Levels",
                  format: "Plenary",
                  instructions:
                    "A quick temperature check before we start building business models.\n\nHow do you feel about completing this phase of the project and presenting it — low tension or high?\n\nPut your number on a post-it, on a scale from 1 (low) to 10 (high), and add it to the wall. No explanation needed.",
                },
                {
                  title: "Intro Business Model Innovation (BMI)",
                  timing: "30 min",
                  format: "Plenary",
                  instructions:
                    "Warm-up: how can cows create value? Milk and meat, manure converted to biogas that powers buses, agri-tourism with farm experiences for families, ceremonial use that generates income while preserving traditions. The same asset supports very different business models.\n\nBusiness model ≠ revenue model\n• Business model = how you create, deliver and capture value. Uber's business model is connecting drivers with passengers through a digital platform.\n• Revenue model = how you turn one of those activities into cash. Uber's revenue model is commission fees, Uber Pass subscriptions and dynamic pricing.\n• Back to the cow: the business model is the whole value creation system (milk, meat, manure, agritourism, ceremonies — value proposition, customer segments and channels). The revenue model is the exact method of monetising one of them: milk sold per litre, a milk subscription, a tourist entry fee, biogas sold to the grid.\n\nSo: a business model is an organisation's logic of creating, delivering and capturing value.\n\nBusiness models don't stay still. Newspapers went from print advertising and subscriptions to diversified, data-driven platforms. Supermarket models evolved as customer demand shifted towards convenience and digital tools arrived.\n\nFor a model to be sustainable: created value > captured value > cost of delivery\n• How can we better solve the problem to increase the value we create?\n• How can we better capitalise on that value to capture more of it?\n• How can we be more efficient in delivering the solution to reduce cost?\n\nThe cow example again: customers value fresh milk at ~€1.20/L, the farmer sells at €1.20 (or €1.00 under competition), and delivery costs ~€0.80/L in feed, milking and packaging.\n\nWhy innovate on the business model at all? Remember the 10 types of innovation — profit model, network, structure, process, product performance, product system, service, channel, brand, customer engagement. Product performance is only one of the ten.",
                },
                {
                  title: "Create Your Business Model",
                  timing: "45 min",
                  format: "Team",
                  toolIds: ["business-model-kit", "vision-card", "concept-card"],
                  instructions:
                    'You\'ll do this in two steps: first get clear on your concept and the value you deliver to your target group, then map the value exchanges between all the stakeholders.\n\nStep 1 — Vision Card (10 min)\nWhy? To refine your innovation idea into a mature, visionary concept.\n• Review your Concept Card and use it as the foundation for your Vision Card.\n• Complete the Vision Card as a group and align on Credibility — discuss why it might rock or why it might fail.\n• Agree your Top 4 Features using common language.\n• Craft your Value Proposition Statement — your crucial mini-pitch — using the builder:\n  "Our ______ (products and services) help(s) ______ (customer segment) who want to ______ (jobs to be done) by ______ (verb, e.g. reducing, avoiding) + ______ (customer pain) and ______ + ______, unlike ______ (competing value proposition)."\n• Then 30 sec to read your Value Proposition Statement out loud.\n\nWorked example (GRONOS, Tesla team 2022): "Our Gronos web application helps people who want to connect to the electricity grid, who want to get clear answers on when and where they can connect, by eliminating long waiting times and reducing manual expert work, unlike slow email exchanges and inefficient processes."\n\nStep 2 — Business Model Kit (30 min)\nWhy? To collaboratively design a clear, actionable business model, so your innovation can create, deliver and capture value effectively.\n• Join your break-out room with your team.\n• Use the Business Model Kit to physically map out your business model: place your stakeholders, then the items they transfer between each other (service, data, money, permission, satisfaction, reputation…).\n• Ensure clarity — it should be clear enough to explain to another team.\n• Document it: take a picture of your completed model and upload it to Miro.\n\nThe GRONOS value exchange map is the reference example: grid connectors give data and get real-time service; platform managers give data entry and get time saved; Elia Business funds the platform and gets money saved and reputation; government gives information and permission.',
                },
              ],
            },
            {
              label: "Lunch break 🍝",
              exercises: [],
            },
            {
              label: "Afternoon",
              exercises: [
                {
                  title: "Batteries",
                  timing: "50 min",
                  format: "Plenary",
                  instructions:
                    "Energiser to re-activate the group after lunch. The facilitator runs a short physical or creative warm-up to reset attention and energy before the afternoon's iteration work.",
                },
                {
                  title: "Business Model Iterations & Selection",
                  timing: "70 min",
                  format: "Team",
                  toolIds: ["business-model-kit", "vision-card"],
                  instructions:
                    "Your first business model is a starting point. Now challenge it — using an AI challenger to find what you can't see yourselves.\n\nYou have 40 min to work.\n\nStep 1 — Run the AI challenger. Photograph your business model map, paste your Value Proposition Statement from the Vision Card into the prompt below, and run it with the photo attached.\n\n———\nWe are running a design sprint for Elia Group, a transmission system operator responsible for high-voltage electricity infrastructure in a regulated market. Attached is a photo of our business model map. The coloured cards are the actors involved. The grey cards are what gets exchanged between them, and the arrows show which direction each thing flows.\n\nOur concept is: [paste VALUE PROPOSITION sentence].\n\nFirst, read the map back to us: list every actor, and for each one what it gives and what it gets. Flag anything you cannot make out.\n\nThen push the model past what we can see ourselves. Do four things:\n\n1. Find the actors missing from our map — whoever is affected by this model, holds power over it, or already makes this work today without appearing on it. Tell us what each would give and what they would need in return.\n\n2. Question what is being exchanged. Show us what else could flow between these actors beyond what we have drawn, and which two actors would trade it.\n\n3. Look outside our industry. Find sectors that solve the same structural problem we are solving, explain the mechanic they use in one sentence, and show us what our model becomes if we borrow it.\n\n4. Name the weakest link in our model — where one side gives more than it gets — and say why it breaks first.\n———\n\nStep 2 — Iterate on your model. Use what came back to create different variations of your business model. Consider how changes in the value proposition, the actors involved, or what flows between them might open new opportunities. You decide what to take and what to reject — the challenger is there to surface blind spots, not to make the call for you.\n\nStep 3 — Discuss. Share and debate how these changes could strengthen, differentiate or expand your business model.\n\nFinal step — Present your changes. 2 minutes per team to explain what you changed and how it improves your business model.\n\nReminder while you work: a business model is an organisation's way of creating, delivering and capturing value.",
                },
                {
                  title: "Business Model Assumptions",
                  timing: "45 min",
                  format: "Team",
                  toolIds: ["assumption-mapper", "business-model-kit"],
                  instructions:
                    "What is the biggest risk in your business model? Every business model has underlying assumptions — validate them as soon as possible.\n\nRecall the triad: desirability assumptions (persona), feasibility assumptions (solution), viability assumptions (business). This afternoon focuses on viability:\n• Does it fit Elia's strategy?\n• Are costs reasonable versus the value created?\n• Can it scale across teams?\n• Is there leadership support?\n\nFour categories of assumptions to look for: client/user focus (desirability), business model (viability), technology (feasibility), and corporate strategy fit.\n\nYou have:\n• 5 min individually\n• 20 min to share in your team\n• 10 min to prioritise\n\nStep 1 — Identify assumptions: add sticky notes to your business model to highlight critical assumptions. Think about what needs to be true for your model to succeed.\n\nStep 2 — Prioritise assumptions: focus on viability, and feasibility where relevant. Use the assumption matrix or simply discuss as a team to identify the most critical assumptions to test.\n\nTwo principles for prioritising: start from user needs, and learn fast from the easy validations first.",
                },
                {
                  title: "Challenge the Other Team",
                  timing: "45 min",
                  format: "Cross-team",
                  toolIds: ["business-model-kit", "assumption-mapper"],
                  instructions:
                    "Time to challenge your business model. Teams pair up — 1 with 2, and 3 with 4.\n\nRoughly, per round:\n• 5 min presenting\n• 10–15 min challenging\nThen switch roles and repeat with the other team presenting.\n\n1/ Present — one team presents their business model, focusing on their most critical business model assumptions.\n\n2/ Challenge — the challengers challenge the presenters' model. Focus on:\n• Do the value exchanges add up?\n• What assumptions were made that need to be validated?\n• Is value created for Elia and for the user(s)?\n\nAs the presenting team: be eager for feedback. The more input you get, the more you have to work with to refine your pitch. This is not the final moment, so use it to identify blind spots and make adjustments.",
                },
                {
                  title: "Update Your Business Model",
                  timing: "30 min",
                  format: "Team",
                  toolIds: ["business-model-kit", "assumption-mapper"],
                  instructions:
                    "Incorporate the feedback from the challenge round. You have 20 min.\n\n• Reflect on feedback — review the challenges and suggestions from the previous exercise.\n• Update your model — adjust your business model to improve viability.\n• Document changes — take a picture of your final business model and upload it to Miro.\n• Align and highlight your critical assumptions to test.\n\nBy the end of Day 1 you should have:\n• A business model you're willing to defend\n• A prioritised list of critical assumptions\n• A clear sense of what you need to prove before Module 4",
                },
              ],
            },
          ],
        },
        {
          day: 2,
          title: "Pitching & Validation Plan",
          timeRange: "9am – 5:30pm",
          subtitle: "Pitching & Validation",
          slideUrl: "/decks/module-3-day-2.pdf",
          description:
            "Day 2 develops the team's ability to communicate and defend the business model. The morning runs PPP, pitching fundamentals, and a working session applying the five pitch building blocks to your own concept. The afternoon covers how to navigate change and influence stakeholders, puts numbers on the value of your solution with ballpark figures, runs a live pitch-and-feedback round, and closes with white space.",
          agenda: {
            morning: [
              { title: "PPP", duration: "45 min" },
              { title: "Pitching intro", duration: "30 min" },
              { title: "Pitching with building blocks", duration: "75 min" },
            ],
            afternoon: [
              { title: "Change / influence", duration: "90 min" },
              { title: "Ballpark figures", duration: "45 min" },
              { title: "Pitching with feedback", duration: "70 min" },
              { title: "White space", duration: "40 min" },
            ],
          },
          sections: [
            {
              label: "Morning",
              exercises: [
                {
                  title: "PPP",
                  timing: "45 min",
                  format: "Plenary",
                  instructions:
                    "A structured opening to align the team on the day ahead before we move into pitching.",
                },
                {
                  title: "Pitching Intro",
                  timing: "30 min",
                  format: "Plenary",
                  toolIds: ["pitching-canvas", "pitching-tips"],
                  instructions:
                    'A pitch is not a presentation — it\'s a persuasion tool. The goal is not to inform, it\'s to move someone to action. So what does a good pitch really need?\n\n5 key pitch building blocks:\n\n#1 Problem to solve — what pain do you solve? Be clear and concise. Make it relatable — use emotions. Support it with data. Highlight the consequences. Set the stage for your solution.\n\n#2 Solution / offering — how do you solve for this need, and how does it add value? The more tangible the better: bring a prototype, do a live demo, don\'t use abstract buzz-lingo — just show your product.\n\n#3 Value of the solution — how will you create value? B2B, internal or B2C, return on investment is all that matters. Frame it as revenue potential ("by investing in our solution you will increase revenue by x% / €x"), cost reduction ("you will decrease costs by x% / €x"), or customer and employee satisfaction ("your customers will become more loyal to you").\n\n#4 Credibility — why should they believe you? Show evidence from your experiments. Even early-stage validation counts: it shows you brought them reliable information.\n\n#5 Next steps / call to action — what are the next steps, and what do you need from the jury? End with an ask, whatever it is. Be clear about your promise and what you expect in return; don\'t leave them guessing what to do.\n\nIn the Tesla programme your ask can be one of four evidence-based decisions, chosen to save resources: Kill, Persevere (iterate until you get solution fit), Pivot (change the scope and repeat the last step), or Invest (go to the next phase of the innovation process).\n\nStoryline logic: every slide should answer a question triggered by the information before it. That\'s what keeps momentum and interest going. If you drop a block, the audience reacts predictably:\n• No problem → confusion\n• No solution → anxiety\n• No value → resistance\n• No credibility → foolishness\n• No call to action → "now what?"\n\nWorked example (energy company):\n• The pain — the company struggles to predict demand, causing waste and blackouts. The audience feels the pain and urgency.\n• Our way out — an AI system forecasts electricity demand using data and weather patterns. They feel relief, but doubt it works.\n• What\'s it worth — AI reduces waste 10–20%, blackout risk 25–40%, emissions 15–30%; millions in savings. They get excited, but wonder: is this true?\n• Why you can trust it — experiments X, Y and Z show these results are realistic. Trust builds. They think: now what?\n• What we do next — start a 6-month pilot to validate assumptions, budget €X, expected value €Y. They think: why wait?',
                },
                {
                  title: "Pitching with Building Blocks",
                  timing: "75 min",
                  format: "Team",
                  toolIds: ["pitching-canvas", "pitching-tips"],
                  instructions:
                    "Start practising your pitching skills by applying the five building blocks to your own concept.\n\nYou have 45 min to prepare.\n\n• Fill in the Pitching Template — one block at a time:\n  1. Problem: what is the problem, who feels it most, how do you make it real and relatable?\n  2. Solution: what does your concept do, how does it work, what makes it tangible?\n  3. Value: what are your strongest value claims, and what data or experiment results support each?\n  4. Credibility: what validation do you have so far, and what proof points are still missing?\n  5. Call to action: what do you want senior leadership to decide at the Module 4 pitch?\n• Then pitch to the group — focus on clearly communicating each building block.\n\nDon't write a script. Build a structure.",
                },
              ],
            },
            {
              label: "Lunch break 🍝",
              exercises: [],
            },
            {
              label: "Afternoon",
              exercises: [
                {
                  title: "Change / Influence",
                  timing: "90 min",
                  format: "Plenary",
                  instructions:
                    "Innovation requires navigating organisational change. Even a well-validated concept fails if you can't bring the right people along.\n\nTwo key questions:\n• Who needs to change their behaviour, belief, or decision for your concept to succeed?\n• How do you influence people who don't report to you?\n\nInfluence levers to consider:\n• Reciprocity — build relationships before you need them\n• Social proof — show who else is moving in this direction\n• Authority — anchor to leadership priorities and strategic goals\n• Scarcity — frame the window of opportunity\n• Commitment — get small agreements that build toward bigger ones\n\nMap the two or three people whose support is most critical to your concept. What's your influence strategy for each?",
                },
                {
                  title: "Ballpark Figures",
                  timing: "45 min",
                  format: "Team",
                  toolIds: ["ballpark-figures", "problem-sizing"],
                  instructions:
                    "Ballpark figures are rough estimates that let you quickly see whether the value is much bigger than the cost — a one-time investment of ~50K against a yearly benefit of ~1M per year, for example. They give you a first indication of profitability potential, a sense of whether the foreseeable outcome is good or bad, and consensus between an uncertain idea and the numbers people will ask for.\n\nWhy do this now rather than a full business case? Because for uncertain projects the traditional approach fails: there's no historical performance to lean on, there are more assumptions than validations, and optimism bias creeps in.\n\nTwo templates — pick the one that fits your concept.\n\nRevenue potential (will your solution generate value?):\n• Buyer or user — who buys your offering, and who uses it? These are not always the same person.\n• Frequency — how often is the offering sold, or used?\n• Potential — how large is the part of the total market you will address? This is the % you used for SOM in problem sizing.\n• Value — what is the pricing of your offering, and what value do you capture?\n⚠️ Focus on early adopter profiles: who is actually going to buy or use your proposition in the next year(s)?\n\nCost savings potential (for internal or incremental innovations, where revenue doesn't make sense):\n• Waste — what waste is caused by this problem?\n• Asset — does the waste come from a user, process, cycle, site or machine?\n• Units of waste per asset, and the % waste reduction you expect\n• Frequency — how often does the problem occur per asset per year?\n• Size — how many of these assets do we have?\n• Cost per unit of waste\nWorked example (replacing in-person site inspection with drones): 1 hour/engineer × 100% reduction × 104 times a year × 10 engineers × €60/hour = €62,400 cost saving potential.\n\nYou have 45 min. Goal: calculate the value of your solution.\n\nStep 1 — Define what you need to know: review the ballpark figures template and write down, in words, what you need to know to calculate a guesstimate for your business model.\nStep 2 — Turn words into numbers (15 min): if you don't have direct estimates, use a benchmark or make an educated guess — but make the underlying assumptions explicit so you can refine them as you learn more.\nStep 3 — Calculate the guesstimate.\n\nThe explicit assumptions matter as much as the number. They become validation targets.",
                },
                {
                  title: "Pitching with Feedback",
                  timing: "70 min",
                  format: "Plenary",
                  toolIds: ["pitching-canvas", "pitch-evaluation-sheet"],
                  instructions:
                    "Now pitch your structured concept to the full group and get it stress-tested.\n\nYou have 30 min to prepare, then per team:\n• 6 min to pitch\n• 4 min of feedback\n\nFocus on clearly communicating each of the five building blocks — including the ballpark figures you just calculated for block #3.\n\nAs the pitching team: listen and capture. The questions you can't answer are your validation gaps, and they become the input for tomorrow's validation plan.",
                },
                {
                  title: "White Space",
                  timing: "40 min",
                  format: "Team",
                  instructions:
                    "Unstructured team time to consolidate the day's work, update your Miro board, align on next steps, or work on anything that needs attention before tomorrow. Coaches are available.\n\nUse this time to:\n• Update your business model with any insights from the pitching sessions\n• Sharpen the claims that got pushed back on\n• Note the assumptions and proof points you'll need for tomorrow's validation plan\n• Flag anything you want to bring to the facilitators",
                },
              ],
            },
          ],
        },
        {
          day: 3,
          title: "Roadmap to Test Assumptions",
          timeRange: "9am – 2:30pm",
          subtitle: "Experimentation",
          slideUrl:
            "https://docs.google.com/presentation/d/1A_nfsoxo2B6QoTKapYtALNKDU-azU-sSHGIky71zcb4/edit",
          description:
            "Day 3 turns the business model into a concrete validation roadmap. The morning introduces the validation plan, works through what to do when an assumption doesn't hold, and gives teams a long working block with coaching to finalise their plan. The afternoon clears white space, locks the entry ticket for Module 4, and closes with a retrospective.",
          agenda: {
            morning: [
              { title: "Validation plan", duration: "30 min" },
              { title: "Work on & finalise validation plan", duration: "2h" },
            ],
            afternoon: [
              { title: "White space", duration: "45 min" },
              { title: "Entry ticket for M4", duration: "15 min" },
              { title: "Retro & wrap-up", duration: "45 min" },
            ],
          },
          sections: [
            {
              label: "Morning",
              exercises: [
                {
                  title: "Validation Plan",
                  timing: "30 min",
                  format: "Team",
                  toolIds: [
                    "validation-guide",
                    "experiment-picker",
                    "assumption-mapper",
                    "ballpark-figures",
                  ],
                  instructions:
                    'Why this matters now:\n• On Day 1 you mapped your business model.\n• On Day 2 you pitched it — but many assumptions were still untested.\n• Before Module 4 we need to validate the riskiest ones.\n\nTurning assumptions into experiments — worked example from the GRONOS model. The transaction "Elia Business funds the platform" carries the assumption: "if Elia funds the platform, it will actually improve their reputation among grid users, because users get faster connections." Two ways to test it:\n• Question people — run a quick survey with grid users: "would faster digital connection approvals improve your perception of Elia?"\n• Behavioural test — put a mock "Powered by Gronos" poster on an application flow and track reactions. Do applicants notice, and do they comment positively?\n\nYou have 20 min to prepare.\n\nGoal: ensure your pitch is backed by solid evidence and validated assumptions.\n\nStep 1 — Remaining assumptions: paste your assumptions from Day 1 into Miro, and define the experiments or actions needed to test them.\nStep 2 — Find missing proof points: review your pitch, especially the ballpark figures (building block #3), and identify any missing proof points or data. Set up actions to gather them.',
                },
                {
                  title: "What If Assumptions Are Not True?",
                  format: "Plenary",
                  toolIds: ["problem-sizing"],
                  instructions:
                    "Testing assumptions is crucial. If an assumption doesn't hold, that's okay — it helps you avoid investing in the wrong direction.\n\nFirst, determine how critical the assumption was to your overall idea or business model. Then pivot, refine, or restart as needed.\n\nScenario #1 — Problem needs nuance: if the problem needs some adjustment, pivot and sharpen the problem. Return to your problem sizing exercise and choose a new angle.\n\nScenario #2 — Problem invalidated: if the problem isn't real, stop. Return to your problem sizing exercise and choose a new problem.\n\nScenario #3 — Solution needs a pivot: if the solution has potential but isn't a perfect fit, pivot. Iterate until you achieve the right fit.\n\nScenario #4 — Solution invalidated: if the solution doesn't work, stop and review. Go back to your decision matrix and select a new solution to test.\n\nScenario #5 — Business model invalidated: if the business model doesn't work, adjust it. Explore different revenue streams, customer segments or channels.\n\nScenario #6 — Solution is too costly: if costs are too high, look for ways to reduce them, or consider alternative solutions that are more cost-effective — a quick win.\n\nIt's okay to start over. Learning that something won't work is just as valuable as success; use that knowledge to guide your next steps. Accept the reality, assess the impact, pivot or adjust, learn and iterate. It's all part of the process.",
                },
                {
                  title: "Work On & Finalise Your Validation Plan",
                  timing: "2h",
                  format: "Team",
                  toolIds: ["experiment-picker", "experiment-card", "validation-guide"],
                  instructions:
                    "A long working block in your break-out room to make the plan execution-ready before you leave Berlin. Coaches come by each team during this block.\n\nStep 1 — Remaining assumptions: review the critical assumptions you identified on Day 1. Which are still untested? For each, confirm the experiment or action, assign an owner, and set a deadline.\n\nStep 2 — Missing proof points: review your pitch building blocks. For each claim that isn't backed by evidence, define what you need and how you'll get it.\n\nStep 3 — Sequencing: order your experiments by risk and dependency. Which assumption, if wrong, changes everything? Test that first.\n\nUpload your finalised validation plan to Miro before you leave.",
                },
              ],
            },
            {
              label: "Lunch break 🍝",
              exercises: [],
            },
            {
              label: "Afternoon",
              exercises: [
                {
                  title: "White Space",
                  timing: "45 min",
                  format: "Team",
                  instructions:
                    "Team working time — use it to complete anything from the morning, update Miro, refine your validation plan, or have the conversations you haven't had time for yet.\n\nFacilitators are available for individual team check-ins during this block.",
                },
                {
                  title: "Entry Ticket for M4",
                  timing: "15 min",
                  format: "Team",
                  instructions:
                    "Review and confirm the three conditions you need to meet before Module 4:\n\n1. Test your most important key assumptions — every critical assumption from your business model should have a result, even if partial.\n2. Find all the missing proof points for your pitch — every claim in your pitch building blocks needs supporting evidence.\n3. Sponsor aligned — your sponsor knows where you are, what you've learned, and what you're planning to pitch.\n\nTwo conversations are built in to help you get there:\n• With your coaches — a session at Board of Innovation on 7 October with Sofie and Domenica. Update them on your problem, solution and business model.\n• With your sponsor — arrange this directly. Ask them for advice on how to navigate internally for your experiments.\n\nAs a team:\n• Rate your current progress on each of the three items (1–5)\n• Identify the biggest gap\n• Assign a team lead and a deadline for each outstanding item\n\nThis is your commitment before you leave Berlin.",
                },
                {
                  title: "Retro & Wrap-up",
                  timing: "45 min",
                  format: "Plenary",
                  instructions:
                    "A structured reflection on Module 3 as a learning experience.\n\nEach participant adds three sticky notes to the shared Miro board:\n• One thing I'm taking away\n• One thing that surprised me\n• One thing I want to do differently\n\nThe facilitator clusters and reads aloud. No attribution — this is collective reflection.\n\nClose with one team commitment: what will you do in the next two weeks to maintain momentum from this module?",
                },
              ],
            },
          ],
        },
      ],
      prints: [
        {
          title: "Business Model Kit",
          description:
            "The physical kit used on Day 1 to build your business model: stakeholder cards plus the items they transfer between each other (service, data, money, permission, satisfaction, reputation). Lay it out on the table, photograph the result, and upload it to Miro.",
        },
        {
          title: "Pitching Template",
          description:
            "One page per pitch building block — problem, solution, value, credibility, call to action — filled in on Day 2 before you pitch to the group.",
        },
        {
          title: "PPP Framework (People, Process, Product)",
          description:
            "A diagnostic lens for assessing your team's way of working across three dimensions: People (roles, skills, motivation), Process (how decisions get made and work flows), and Product (the quality and direction of what you're building). Use it to spot where friction is coming from before it derails delivery.",
        },
        {
          title: "ADKAR Diagnostic",
          description:
            "A change-management diagnostic — Awareness, Desire, Knowledge, Ability, Reinforcement — for assessing how ready your team, sponsor, or organisation is to adopt the solution you're building. Pinpoints exactly which stage is the current blocker to traction.",
        },
        {
          title: "Vision Card (A3)",
          description:
            "Clarify and communicate the vision behind your solution in 9 blocks — who it's for, the problem, what you offer, revenue ideas, why it will work or fail, top features, next steps, and a value proposition statement.",
          url: "https://drive.google.com/file/d/15iFnY2vvjQYjw7Og6Yb5FsOag9qgo_Ll/view",
        },
        {
          title: "Ballpark Figures (A3)",
          description:
            "A back-of-envelope revenue model — buyer/user × frequency × addressable market × price — to agree on a handful of key assumptions rather than a detailed financial model.",
          url: "https://drive.google.com/file/d/1xOK_DW8n5hmNRAeTlELIFBkXGhtP4g_x/view",
        },
        {
          title: "Validation Guide (A4)",
          description:
            "Reference guide to all 24 experiment types — when to use each, how to run it, and common pitfalls. Useful when designing the validation experiments behind your business model.",
          url: "https://drive.google.com/file/d/1xr6hLtk_0296lIkqqsMUvtY7tKNoQ4mY/view",
        },
      ],
    },
    after: {
      accomplishments:
        "You've stress-tested your concept against business reality and built a model that shows how it creates, delivers and captures value. You leave with a mapped business model, ballpark figures for the value of your solution, a prioritised set of critical assumptions, and a validation plan to execute before Module 4.",
      betweenModuleWork: [
        "Test your most important key assumptions",
        "Find all the missing proof points for your pitch",
        "Sponsor aligned — arrange the conversation directly, and ask for advice on how to navigate internally for your experiments",
        "Coaching session with Sofie and Domenica at Board of Innovation on 7 October — update them on your problem, solution and business model",
      ],
      resources: [
        {
          title: "Business Model & Assumptions",
          description:
            "The photo of your business model and your prioritised assumption list, both in Miro. Update them as experiment results come in, so your Module 4 pitch reflects what you actually learned.",
        },
        {
          title: "Ballpark Figures",
          description:
            "Refine your revenue or cost-savings guesstimate as you gather real numbers — the explicit assumptions underneath it are validation targets in their own right.",
          url: "https://drive.google.com/file/d/1xOK_DW8n5hmNRAeTlELIFBkXGhtP4g_x/view",
        },
        {
          title: "Vision Card",
          description:
            "Use this to clarify and sharpen your solution vision before building your pitch.",
          url: "https://drive.google.com/file/d/1x0L5jEJMrsCZ5HZQZ7W6Iz2z7eW24a7i/view",
        },
        {
          title: "Validation Guide",
          description:
            "Reference for running any remaining experiments and collecting proof points for your pitch.",
          url: "https://drive.google.com/file/d/1xr6hLtk_0296lIkqqsMUvtY7tKNoQ4mY/view",
        },
      ],
      nextModulePreview:
        "Module 4 in Brussels is your final performance. You'll craft and deliver a compelling pitch, receive structured feedback from a panel, and translate everything you've learned into concrete next steps for your role and organisation.",
    },
    facilitator: {
      agenda: [
        { type: "day", name: "DAY 1", startTime: "09:00" },
        { type: "activity", name: "Intro", duration: "15 min" },
        {
          type: "activity",
          number: "1",
          name: "Psychological safety",
          duration: "60 min",
          owner: "Pieter",
        },
        { type: "activity", number: "2", name: "Share-out", duration: "35 min", owner: "Sofie" },
        { type: "activity", name: "Coffee break", duration: "10 min" },
        {
          type: "activity",
          number: "3",
          name: "Intro business model",
          duration: "30 min",
          owner: "Sofie",
        },
        {
          type: "activity",
          number: "4",
          name: "Create your business model",
          duration: "45 min",
          owner: "Sofie",
          comment: "start with vision card & value proposition",
        },
        { type: "lunch", name: "Lunch", duration: "45 min" },
        { type: "activity", number: "5", name: "Batteries", duration: "50 min", owner: "Pieter" },
        {
          type: "activity",
          number: "6",
          name: "BMI design, iterations",
          duration: "70 min",
          owner: "Dome",
        },
        { type: "activity", name: "Coffee break", duration: "15 min" },
        {
          type: "activity",
          number: "7",
          name: "Business Model assumptions",
          duration: "45 min",
          owner: "Dome",
        },
        {
          type: "activity",
          number: "8",
          name: "Challenge the other team",
          duration: "45 min",
          owner: "Dome",
        },
        {
          type: "activity",
          number: "9",
          name: "Update BMI based on feedback",
          duration: "30 min",
          owner: "All",
        },
        { type: "activity", name: "Wrap up", duration: "15 min", owner: "All" },
        { type: "activity", name: "Travel", duration: "30 min" },
        { type: "activity", number: "10", name: "Dinner", duration: "2h", owner: "All" },
        { type: "day", name: "DAY 2", startTime: "09:00" },
        { type: "activity", name: "Intro", duration: "15 min", owner: "All" },
        { type: "activity", number: "1", name: "PPP", duration: "45 min", owner: "Pieter" },
        {
          type: "activity",
          number: "2",
          name: "Pitching intro",
          duration: "30 min",
          owner: "Dome",
        },
        { type: "activity", name: "Coffee break", duration: "15 min" },
        {
          type: "activity",
          number: "3",
          name: "Pitching with building blocks",
          duration: "75 min",
          owner: "Dome",
        },
        { type: "lunch", name: "Lunch", duration: "1h" },
        {
          type: "activity",
          number: "4",
          name: "Change / influence",
          duration: "90 min",
          owner: "Pieter",
        },
        {
          type: "activity",
          number: "5",
          name: "Ballpark figures",
          duration: "45 min",
          owner: "Sofie",
        },
        { type: "activity", name: "Coffee break", duration: "15 min" },
        {
          type: "activity",
          number: "6",
          name: "Pitching with feedback",
          duration: "70 min",
          owner: "All",
        },
        { type: "activity", number: "7", name: "White space", duration: "40 min", owner: "All" },
        { type: "activity", name: "Wrap up", duration: "10 min" },
        { type: "day", name: "DAY 3", startTime: "09:00" },
        { type: "activity", name: "Intro", duration: "15 min" },
        {
          type: "activity",
          number: "1",
          name: "Validation Plan",
          duration: "30 min",
          owner: "All",
          comment: "Show validation plan in Miro",
        },
        {
          type: "activity",
          number: "2",
          name: "Work on validation plan",
          duration: "2h",
          owner: "All",
        },
        { type: "lunch", name: "Lunch", duration: "1h" },
        { type: "activity", number: "3", name: "White space", duration: "45 min", owner: "All" },
        {
          type: "activity",
          number: "4",
          name: "Entry ticket for M4",
          duration: "15 min",
          owner: "All",
        },
        {
          type: "activity",
          number: "5",
          name: "Retro & Wrap up",
          duration: "45 min",
          owner: "All",
        },
      ],
      checklist: [
        {
          category: "Agenda & content",
          items: [
            { text: "Review agenda (Facilitation Tool is now leading)", checked: true },
            {
              text: "Adjust ppt deck according to new digital agenda (that's leading)",
              owner: "Sofie",
            },
            {
              text: "Decide on presenting something on day 1?",
              owner: "Dome / Sofie",
              checked: true,
            },
            "Include time to prep pitch on day 1 morning",
            "Share message of what you have done - no need to have a deck",
            "Day 1 afternoon: instruct teams to follow exercises on website, let coaches come by after 1st business model version",
            {
              text: "Move 'what if assumptions are not true' to the beginning of the module",
              owner: "Sofie",
            },
            { text: "Divide sections (on Monday 31st)", owner: "Dome / Sofie" },
          ],
        },
        {
          category: "Participants & coaching",
          items: [
            {
              text: "Check in with teams if solutions are invalidated? Determines the speed in which they can proceed",
              checked: true,
            },
            { text: "Should teams check in with the nest between M3 and M4?", owner: "Sofie" },
            {
              text: "Introduce website to participants - introduce in module",
              owner: "Dome / Sofie",
            },
          ],
        },
        {
          category: "Facilitation Tool & website",
          items: [
            {
              text: "Update Kevin & Joke on website - check if there are things to take into account",
              owner: "Dome",
            },
            {
              text: "Improve agenda in Facilitation Tool with colours and comments, plus ability to edit the checklist",
              owner: "Dome",
            },
            { text: "Share Tool access with Sofie and Pieter", owner: "Dome" },
            {
              text: "Share agenda in Tool with Pieter - Pieter to approve on his working blocks",
              owner: "Dome",
            },
          ],
        },
        {
          category: "Logistics & materials",
          items: [
            { text: "Check on dinner reservation module 3", owner: "Sofie" },
            { text: "Invite Maxi for pitches", owner: "Sofie → Joke/Kevin" },
            {
              text: "Bring business model kits, post-its, tape and sharpies to Berlin",
              owner: "Dome",
            },
          ],
        },
      ],
      slideUrls: [
        {
          day: 1,
          label: "Day 1 — Business Model Innovation",
          url: "/decks/module-3-day-1.pdf",
        },
        {
          day: 2,
          label: "Day 2 — Pitching & Validation Plan",
          url: "/decks/module-3-day-2.pdf",
        },
        {
          day: 3,
          label: "Day 3 — Roadmap to Test Assumptions",
          url: "https://docs.google.com/presentation/d/1A_nfsoxo2B6QoTKapYtALNKDU-azU-sSHGIky71zcb4/edit",
        },
      ],
      slideDecks: [],
    },
  },
  {
    id: 4,
    title: "Pitching",
    theme: "Making the case for action",
    location: "Brussels",
    month: "November",
    outcome:
      "A polished pitch deck and narrative, delivered to a live panel, with a concrete implementation plan for taking the work forward.",
    before: {
      overview:
        "Module 4 is the culmination of everything you've built. You'll craft a compelling story around your innovation — one that's honest about uncertainty, rigorous about evidence, and clear about what you're asking decision-makers to do. Come ready to pitch, receive hard feedback, and leave with a plan.",
      preparation: [
        "Test your most important key assumptions",
        "Find all the missing proof points for your pitch",
        "Sponsor aligned",
      ],
      carryOver:
        "Your validated Business Model Canvas, validation experiment results, and refined Concept Napkin from Module 3 are the core content of your pitch. Make sure they're current and accessible.",
    },
    during: {
      sessions: [
        {
          day: 1,
          title: "Pitch Craft & Storytelling",
          timeRange: "9am – 6pm",
          subtitle: "Crafting",
          description:
            "A strong pitch is not a summary of your work — it's a compelling argument for action. Today you recap the five building blocks every pitch needs, draft your storyline on post-its, and run it past your peers before stress-testing it through five quality-check 'hats' and a verbal & visual communication tips session.",
          agenda: {
            morning: [
              { title: "Energiser", duration: "15 min" },
              { title: "Introduction of Day 1", duration: "10 min" },
              { title: "CSP (Check-in)", duration: "15 min" },
              { title: "Building Blocks Recap: The 5 Pitch Parts", duration: "30 min" },
              { title: "Prepare Pitch on Post-its", duration: "30 min" },
              { title: "Break-out Pitch Practice", duration: "1h" },
              { title: "Content Refinement (Tweak the Pitch)", duration: "45 min" },
            ],
            afternoon: [
              { title: "Put on the Hat of… (5 Hats Quality Check)", duration: "45 min" },
              { title: "Pitching Tips: Verbal & Visual Communication", duration: "30 min" },
              { title: "White Space: Build & Dry-Run Your Pitch", duration: "3h" },
              { title: "Wrap-up & Reflection", duration: "15 min" },
            ],
          },
          sections: [
            {
              label: "Morning",
              exercises: [
                {
                  title: "Building Blocks Recap: The 5 Pitch Parts",
                  timing: "30 min",
                  format: "Plenary",
                  toolIds: ["pitching-canvas"],
                  instructions:
                    "Every pitch needs five parts — don't miss one:\n\n1. Problem to solve: describe the issue your idea addresses and why it matters, using statistics or real examples. How to pitch it: make it relatable and urgent — paint the impact of not solving it.\n2. Solution: explain how your idea directly addresses the problem. How to pitch it: emphasise what's unique, bring a prototype or live demo, avoid abstract buzzwords.\n3. Value of the solution: the tangible benefits and ballpark figures. How to pitch it: use metrics or case studies to show effectiveness, cost savings, or ROI.\n4. Credibility: the evidence, validation, and expertise that make your solution believable.\n5. Call to action: state clearly what you're asking for and what the next steps are. How to pitch it: avoid ambiguity — spell out the specific action you want and the roadmap for getting there.",
                },
                {
                  title: "Prepare Pitch on Post-its",
                  timing: "30 min",
                  format: "Team",
                  toolIds: ["pitching-canvas"],
                  instructions:
                    "Organise and clarify your pitch on post-it notes before presenting.\n\n• Use the Pitching Canvas and a pitch checklist.\n• Prepare a 7-minute version of your pitch using one post-it per building block (slides optional at this stage).\n• Focus on the storyline connecting all five building blocks — don't jump straight to slides.\n• Break out into your team room to work.",
                },
                {
                  title: "Break-out Pitch Practice",
                  timing: "1h",
                  format: "Breakout rooms",
                  instructions:
                    "Practice delivering your pitch within a set time, focused on the storyline and all five building blocks.\n\n• Each team gets 7 minutes to pitch, as in the real scenario.\n• Gather in the shared practice space for a first read-through.\n• Facilitators/coaches give feedback in an individual team setting afterwards.\n• If you're observing another team, write down feedback and hand it to them once they're done.",
                },
                {
                  title: "Content Refinement (Tweak the Pitch)",
                  timing: "45 min",
                  format: "Team",
                  instructions:
                    "Refine your pitching content as a team to prepare for the next steps.\n\n• Use this time to work collaboratively on improving your pitch.\n• Reach out to a coach if you need assistance.\n• Check in on progress before moving on and flag any additional support you need.\n• Work in your assigned breakout room.",
                },
              ],
            },
            {
              label: "Lunch break 🍝",
              exercises: [],
            },
            {
              label: "Afternoon",
              exercises: [
                {
                  title: "Put on the Hat of… (5 Hats Quality Check)",
                  timing: "45 min",
                  format: "Team",
                  instructions:
                    "Assign each of the 5 hats to a team member so everyone owns one quality check on your pitch:\n\n1. Audience hat: consider your audience's needs and pain points. What does the jury care about most? Why should they listen — what's in it for them?\n2. Goal hat: define what you want to achieve with this pitch. What specific outcome are you aiming for, and how will you measure success?\n3. Story hat: craft a story that ties your solution to the audience's needs. What story best demonstrates the value of your solution?\n4. Delivery hat: deliver confidently with tone, body language, and timing. How can tone and gestures strengthen your message? What should your pacing look like?\n5. Visual hat: make sure every visual reinforces the other four hats. Do the visuals emphasise the message and story? Are slides uncluttered and easy to follow?\n\nGo through your pitch once with each hat on, tweaking as you go.",
                },
                {
                  title: "Pitching Tips: Verbal & Visual Communication",
                  timing: "30 min",
                  format: "Individual / Team reference",
                  toolIds: ["pitching-tips"],
                  instructions:
                    "A quick-reference session before you build your slides and rehearse delivery.\n\nVerbal — things to do: repeat your most important points; use silence to land a statement; ask a short question rather than a long discussion to keep the audience active; save your highest energy for your key messages; use metaphors to make complex ideas stick; check your posture and gestures by recording a dry run; a bit of shaking is normal — hold something or rest your hand to reduce it; use your Q&A time honestly, it's fine to say 'we don't know that yet.'\n\nVerbal — things to avoid: don't mention internal challenges, corporate politics, or excuses ('my slides aren't finished...', 'I'm tired today...'); don't open with an apology — start with confidence instead; be careful with jokes unless you're sure the room will like them.\n\nVisual — things to do: give your slides room to breathe; one key message per slide; keep a consistent visual identity (colours, fonts, style); put the most important element at the top of each slide; use animations sparingly; keep fonts legible from a distance.\n\nVisual — things to avoid: perfection paralysis — just get something on the page and iterate; overcrowded slides; overly complex graphs; clashing colours; tiny or overly decorative fonts; irrelevant content that doesn't support your key message.\n\nRemember: you are the presentation — the deck only supports you.",
                },
                {
                  title: "White Space: Build & Dry-Run Your Pitch",
                  timing: "3h",
                  format: "Team + Coaches",
                  instructions:
                    "Dedicated, coached time to take your pitch from storyline to stage-ready.\n\n• Roles & responsibilities (10 min): assign who wears which hat, who pitches which building block, who builds the slides, and who preps the Q&A.\n• White space (2h): build your pitch story, slides, and Q&A responses. Coaches circulate to give feedback — run the 5-hats quality check again as you go.\n• Dry-runs (remaining time, ~20 min per team): pitch to a coach in the practice space and get feedback. While another team presents, stay in your breakout room and keep refining.",
                },
              ],
            },
          ],
        },
        {
          day: 2,
          title: "Official Pitch & Jury Carousel",
          timeRange: "9am – 6pm",
          subtitle: "Pitching",
          description:
            "Each team delivers its full pitch to a jury of senior sponsors and executives, followed by structured Q&A and team reflection. In the afternoon, teams rotate through a carousel of one-on-one conversations with jury members before celebrating together over dinner.",
          agenda: {
            morning: [
              { title: "Check-in", duration: "15 min" },
              { title: "Today's Practicalities Briefing", duration: "15 min" },
              { title: "Final Sprint: White Space & Coaching", duration: "2h" },
              { title: "Dry-Runs in the Practice Space", duration: "2h" },
            ],
            afternoon: [
              { title: "Official Pitch: Live Presentations to the Jury", duration: "~3h" },
              { title: "Carousel with the Jury", duration: "1h 15 min" },
              { title: "Dinner celebration", duration: "Evening" },
            ],
          },
          sections: [
            {
              label: "Morning",
              exercises: [
                {
                  title: "Final Sprint: White Space",
                  timing: "2h",
                  format: "Team + Coaches",
                  instructions:
                    "Finalise and fine-tune your pitch through dedicated preparation and coaching.\n\n• Pitch refinement: work on final adjustments to content and delivery.\n• Coaching slots: each team gets at least two 15-minute coaching sessions.\n• Optional dry-run: book a practice-space slot to rehearse the full pitch before the deadline to share your slides.",
                },
              ],
            },
            {
              label: "Lunch break 🍝",
              exercises: [],
            },
            {
              label: "Afternoon",
              exercises: [
                {
                  title: "Official Pitch to the Jury",
                  timing: "~30 min per team",
                  format: "Plenary",
                  toolIds: ["pitch-evaluation-sheet"],
                  instructions:
                    "Each team's slot runs:\n\n• 7 minutes — team pitch\n• 2–4 minutes — sponsor reflection: your team sponsor shares their experience and insights\n• 10 minutes — jury feedback incl. Q&A (roughly 3 min per jury member)\n• 5 minutes — team reflections: each team member shares their top learning from the program\n\nThe jury scores each pitch against: problem clarity, strength of insight, business model viability, validation quality, and clarity of ask (from the Pitch Evaluation Sheet).",
                  facilitatorNotes:
                    "Guiding questions to hand the jury before each Q&A: (1) What did you like? Think of two things. (2) What could be improved? Think of two things. (3) What questions do you still have for the team? Think of two things. Confirm jury composition in advance (aim for a senior, cross-functional panel) and share a one-page briefing with the guiding questions and schedule.",
                },
                {
                  title: "Carousel with the Jury",
                  timing: "1h 15 min",
                  format: "Rotating small groups",
                  instructions:
                    "Teams rotate through short one-on-one conversations with each jury member — a chance to go deeper than the Q&A allowed.\n\nConversation starters to bring:\n• Next steps: what could be effective next steps to bring this project further? (pilots, more validation, collaboration with other teams)\n• Project needs: what do you need to execute those next steps — data access, cross-team support, funding?\n• Impact alignment: how does this project contribute to the organisation's broader goals or priorities?\n\nEach rotation runs ~15 minutes; keep a note of the advice you get from each jury member to bring into tomorrow's reflection.",
                },
              ],
            },
          ],
        },
        {
          day: 3,
          title: "To-Job Translation & Next Steps",
          timeRange: "9am – 3pm",
          subtitle: "Implementation",
          description:
            "The Tesla Program ends here, but the real work begins when you return to your organisations. This session is about translating your innovation experience into concrete actions, navigating implementation challenges, and staying connected as an alumni community.",
          agenda: {
            morning: [
              { title: "Energiser", duration: "15 min" },
              { title: "Introduction of Day 3", duration: "10 min" },
              { title: "From program to practice", duration: "30 min" },
              { title: "90-Day Implementation Sprint Plan", duration: "1h 15 min" },
            ],
            afternoon: [
              { title: "Peer accountability pairing", duration: "30 min" },
              { title: "Program retrospective", duration: "30 min" },
              { title: "Celebration & close", duration: "45 min" },
            ],
          },
          sections: [
            {
              label: "Morning",
              exercises: [
                {
                  title: "90-Day Implementation Sprint Plan",
                  timing: "1h 15 min",
                  format: "Individual",
                  instructions:
                    "Design your personal 90-day plan for taking this innovation work forward in your role.\n\nThree milestones (pick specific outcomes, not activities):\n1. A stakeholder outcome — e.g. 'CEO has been briefed and agreed to a feasibility review'\n2. A project outcome — e.g. 'First low-fidelity prototype tested with 5 users'\n3. A personal outcome — e.g. 'Weekly innovation thinking habit established'\n\nFor each milestone:\n• Key stakeholders to engage\n• Resources you'll need (budget, people, access)\n• One thing you'll stop doing to make space\n\nShare your plan with the group in 90 seconds. Your fellow participants are now your accountability network — they'll remember what you committed to.",
                },
              ],
            },
            {
              label: "Afternoon",
              exercises: [
                {
                  title: "Peer Accountability Pairing",
                  timing: "30 min",
                  format: "Pairs",
                  instructions:
                    "Pair with someone from a different team — ideally someone whose work is not the same as yours.\n\nExchange:\n• Your single most important 90-day commitment\n• One thing that could get in the way\n• How you'd like to be held accountable\n\nAgree to a 30-minute check-in call in 6 weeks. Put it in your calendars before leaving the room.\n\nAccountability works best when it's specific ('I committed to doing X by Y') and when the person knows what you need — challenge, support, or just a listening ear.",
                },
                {
                  title: "Program Retrospective",
                  timing: "30 min",
                  format: "Plenary",
                  instructions:
                    "A shared reflection on the Tesla Program as a learning experience.\n\nEach participant adds three sticky notes to the Miro board:\n• One thing that will change how you work\n• One thing the program could do better\n• One unexpected thing you learned about yourself\n\nThe facilitator clusters and reads aloud. No attribution — this is collective reflection, not evaluation.\n\nFeedback is used to improve the next cohort. Your candour directly improves the program for the next group of Tesla participants.",
                },
              ],
            },
          ],
        },
      ],
      prints: [
        {
          title: "Pitch Checklist (A3)",
          description:
            "Slide-by-slide structure for a compelling innovation pitch, with prompts and examples for each section.",
          url: "https://drive.google.com/file/d/1WIU6K-vgBgnZtvW6gP3rudjzv6PUN0-a/view",
        },
        {
          title: "CSP Check-in (Comfort, Stretch, Panic)",
          description:
            "A reflection framework for checking in on where you sit across three zones — Comfort, Stretch, and Panic (freezing up or detaching from the goal, the risk, or the team) — and where you can lean into a productive stretch for this module. Reflect individually or as a team with prompts like: how are you feeling today? How does that compare to the start of Module 1? What's been the most extreme moment throughout the program so far? For a playful format, fold the questions into a paper fortune teller ('happertje') and use it to pick who answers what.",
        },
        {
          title: "Pitching Tips: Verbal & Visual Communication",
          description:
            "Dos and don'ts for delivering and designing your pitch — repeating key points, using silence and metaphors, managing nerves, one message per slide, consistent visual identity, and more.",
          url: "https://drive.google.com/file/d/1ADx5MgGo46-aP8PlSLETGeqGC564prbA/view",
        },
        {
          title: "Pitching Canvas (A1)",
          description:
            "Structure your pitch storyline across six sections: problem, solution, value, credibility, call to action, and Q&A prep. Build the story here before touching the deck.",
          url: "https://drive.google.com/file/d/17h7FQcWTyc8p2QS87mi7ycBO2uNfNCXK/view",
        },
        {
          title: "Pitch Evaluation Sheet (A4)",
          description:
            "Scoring tool for dry-run sessions and pitch day, evaluating five dimensions with a Big No → Big Yes rating scale.",
          url: "https://drive.google.com/file/d/1IYxa5zXsC1Eonq6dH1R57yoXgNzAO5rj/view",
        },
      ],
    },
    after: {
      accomplishments:
        "You've brought your full innovation journey to a close with a pitch to senior leadership. You leave with a presentation-ready narrative, a concrete recommendation, and a leadership team that has seen your work.",
      betweenModuleWork: [],
      resources: [
        {
          title: "Pitching Canvas",
          description:
            "Structure your pitch storyline across six sections: problem, solution, value (with ballpark figures), credibility, call to action, and Q&A prep. Build the story here before touching the deck.",
          url: "https://drive.google.com/file/d/17h7FQcWTyc8p2QS87mi7ycBO2uNfNCXK/view",
        },
        {
          title: "Pitch Evaluation Sheet",
          description:
            "Scoring tool for dry-run sessions and pitch day. Evaluates five dimensions — problem clarity, solution quality, business model, credibility, and next steps — with a Big No → Big Yes rating scale.",
          url: "https://drive.google.com/file/d/1IYxa5zXsC1Eonq6dH1R57yoXgNzAO5rj/view",
        },
        {
          title: "Pitching Tips: Verbal & Visual Communication",
          description:
            "Dos and don'ts for delivering and designing your pitch — repeating key points, using silence and metaphors, managing nerves, one message per slide, consistent visual identity, and more.",
          url: "https://drive.google.com/file/d/1ADx5MgGo46-aP8PlSLETGeqGC564prbA/view",
        },
        {
          title: "Free design tools for your slides",
          description:
            "Pictures: unsplash.com, pexels.com. Icons & PowerPoint templates: canva.com, slidesgo.com, thenounproject.com, flaticon.com.",
        },
        {
          title: "Made to Stick by Chip & Dan Heath",
          description:
            "A masterclass in why some ideas survive and others die. Directly applicable to refining your pitch narrative and making your innovation case memorable.",
        },
        {
          title: "The Innovator's Dilemma by Clayton Christensen",
          description:
            "Essential reading for navigating the internal politics of bringing an innovation to an established organisation.",
        },
        {
          title: "Tesla Program Alumni Network",
          description:
            "Connect with Tesla Program alumni from previous cohorts. Access case studies, find collaborators, and tap into the Tesla Program practitioner community.",
        },
      ],
      nextModulePreview: null,
    },
    facilitator: {
      checklist: [
        {
          category: "Room & logistics",
          items: [
            "Book main room for pitch day (theatre-style seating, presentation screen)",
            "Book breakout rooms for dry-run sessions (1 per team)",
            "Confirm jury / panel availability and send briefing doc",
            "Confirm catering for pitch day (includes jury lunch)",
            "Prepare scoring sheets for jury members",
            "Set up carousel stations for the jury rotation (1 station per jury member)",
            "Book dinner reservation for the evening after the pitch",
          ],
        },
        {
          category: "Materials to print",
          items: [
            "Pitching Canvas — 1× A1 per team",
            "Pitch Evaluation Sheet — 1× A4 per jury member × number of teams",
            "5 Hats Quality Check Card — 1× per team",
            "Pitching Tips (verbal & visual communication) one-pager — 1× per participant",
            "Program overview one-pager for jury",
            "Carousel schedule and conversation-starter cards for jury members",
          ],
        },
        {
          category: "Tech & tools",
          items: [
            "Test AV setup in pitch room (HDMI, clicker, backup laptop)",
            "Set up shared pitch deck folder — each team uploads by agreed deadline",
            "Confirm Teams/Zoom for any remote jury members",
          ],
        },
        {
          category: "Entry ticket check",
          items: [
            "Confirm each team has tested key assumptions",
            "Confirm proof points / evidence gathered",
            "Confirm sponsor is aligned and briefed on pitch day",
            "Confirm pitch decks submitted 24h before pitch day",
          ],
        },
      ],
      slideUrls: [
        {
          day: 1,
          label: "Day 1 — Pitch Preparation & Dry Run",
          url: "https://drive.google.com/drive/folders/1_TeslaProgramM4D1",
        },
        {
          day: 2,
          label: "Day 2 — Pitch Day",
          url: "https://drive.google.com/drive/folders/1_TeslaProgramM4D2",
        },
      ],
      slideDecks: [],
    },
  },
];
